import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Table, Card, message, Spin, Select, Tabs, Button, Tooltip, Segmented } from 'antd';
import { CrownFilled, InfoCircleOutlined, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery } from '@apollo/client';
import { showError } from '../../../utils/exceptionUtils';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import TimeRangeFilter, { TimeRange } from '../../../components/dashboard/TimeRangeFilter';
import { DASHBOARD_PACIFIC_TZ, resolveDashboardComparisonAvgLabel, formatHubDisplayLabel } from '../../../utils/dashboardPacificTime';
import { QUERY_SCALE_DASHBOARD } from '../../../graphql/queries/dashboard/queryScaleDashboard';
import { QUERY_SCALE_DASHBOARD_HISTORICAL_MAX } from '../../../graphql/queries/dashboard/queryScaleDashboardHistoricalMax';
import {
    DASHBOARD_EXCLUDE_HUB_LABELS,
    useOperableHubSelectOptions,
} from '../../../hooks/useOperableHubSelectOptions';
import {
    ScaleDashboardItem,
    ScaleDashboardQueryInput,
    ScaleDashboardHistoricalMaxQueryInput,
    QueryScaleDashboardHistoricalMaxResponse,
    QueryScaleDashboardResponse,
    ScaleDashboardMetric,
    ScaleQueryType,
    OrderFlowType,
    ScaleDashboardSubTab,
} from '../../../graphql/types/dashboard/ScaleDashboardTypes';
import { scaleItemHubId, scaleItemSortingCode } from './scaleDashboardItemUtils';
import {
    GENERATE_DASHBOARD_TABLE_EXPORT,
    GenerateDashboardTableExportResponse,
    GenerateDashboardTableExportVariables,
} from '../../../graphql/mutations/dashboard/generateDashboardTableExport';
import { dashboardTableExportCellString } from '../../../utils/dashboardTableExportCellString';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';
import MetricAnnotationCell from '../../../components/dashboardAnnotation/MetricAnnotationCell';
import { useDashboardAnnotations } from '../../../components/dashboardAnnotation/useDashboardAnnotations';
import { buildAnnotationKey } from '../../../components/dashboardAnnotation/buildAnnotationKey';
import {
    dashboardAnnotationRegistry,
    PAGE_KEY_SCALE,
    type CellContext,
    type DashboardAnnotationColumnType,
} from '../../../components/dashboardAnnotation/registry';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);


/** 安全解析周期字符串为美西日期，避免 Invalid time value；解析失败返回 null */
function safeParsePeriodInTz(periodStr: string, tz: string): dayjs.Dayjs | null {
    try {
        const d = dayjs.tz(periodStr, tz);
        return d != null && d.isValid() ? d : null;
    } catch {
        return null;
    }
}

// 深紫、中紫、浅紫（表头层级背景色，整体调浅）
/** 历史最高值回溯天数，与后端 ScaleDashboardQuery.HISTORICAL_MAX_LOOKBACK_DAYS 保持一致 */
const HISTORICAL_MAX_LOOKBACK_DAYS = 90;

const PURPLE_DEEP = '#E2DFF7';
const PURPLE_MID = '#ECEAF9';
const PURPLE_LIGHT = '#F9F8FF';

// 格式化数字：整数不带小数，小数保留1位，千分位
const formatNumber = (num: number | undefined | null): string => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    if (Number.isInteger(num)) {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    const rounded = Math.round(num * 10) / 10;
    if (Number.isInteger(rounded)) {
        return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    const fixed = rounded.toFixed(1);
    const [integerPart, decimalPart] = fixed.split('.');
    return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart}`;
};

// 环比保留一位小数（从上一期均值的百分比）
const formatChange = (change?: string | null): React.ReactNode => {
    if (change == null || change === '-') return '-';

    // 先判断符号
    const isNegative = String(change).startsWith('-');
    const isPositive = String(change).startsWith('+');

    // 再提取数字部分（去除符号和百分号）
    const numStr = String(change).replace('%', '').replace(/[+\-]/g, '').trim();
    const num = parseFloat(numStr);
    if (isNaN(num)) return change;

    const rounded = Math.round(num * 10) / 10;

    // 环比红色与分拣数据异常指标一致 #ff4d4f
    const prefix = isNegative ? '-' : isPositive ? '+' : (rounded > 0 ? '+' : '');

    return (
        <span
            className={isPositive || rounded > 0 ? 'text-green-600' : ''}
            style={isNegative ? { color: '#ff4d4f' } : undefined}
        >
            {prefix}{rounded}%
        </span>
    );
};

interface DataRow {
    key: string;
    label: string;
    indent: 0 | 1 | 2 | 3; // 0 一级 4空格 8空格 12空格
    headerBg?: 'deep' | 'mid' | 'light';
    /** 可展开行（"其他客户"聚合行 / 具体非命名客户行）：label 列渲染 ▶/▼ 图标 */
    expandable?: boolean;
    /** 父行 key：祖先链全部展开时该行才显示 */
    parentKey?: string;
    [colKey: string]: string | number | boolean | undefined | null;
}

// 客户类型选项：1 跨境专线，2 本地揽收
const CUSTOMER_TYPE_OPTIONS = [
    { value: null as number | null, label: '全部' },
    { value: 1, label: '跨境专线' },
    { value: 2, label: '本地揽收' },
];

/**
 * 后端 customerType 4 类：1 跨境专线 / 2 本地揽收 / 3 本地客户 / 4 SMB 客户。
 * 前端目前只展示 2 个分组，业务要求把 3/4 归入「本地揽收」下，避免数据丢失。
 * 归并后所有下游聚合（pivot / buildCustomerSection / 导出）只看到 customerType ∈ {1, 2}。
 */
function normalizeScaleItem(item: ScaleDashboardItem): ScaleDashboardItem {
    if (item.customerType === 3 || item.customerType === 4) {
        return { ...item, customerType: 2 };
    }
    return item;
}

/**
 * 后端 buildWhere 已经把 customerType=2 改写成 customer_type IN (2,3,4)，
 * 所以前端可以直接透传选择值，所有 subTab 都能拿到正确的归并语义；
 * 尾程下单监控（前端不再二次 normalize）也由此修复。
 */
function effectiveCustomerTypeForQuery(selected: number | null | undefined): number | undefined {
    return selected ?? undefined;
}

// 本地揽收下单独展示的客户名称
const LOCAL_NAMED_CUSTOMERS = ['Temu半托管', 'TikTok CBT', 'TikTok FBT', '出口易'] as const;
// 跨境专线下单独展示的客户名称
const CROSS_NAMED_CUSTOMERS = ['Temu全托管', '顺丰', 'WANB', '京东物流', '急速国际2', '汇发', 'CNE递一', 'TikTok GS'] as const;
// 客户名称固定选项（与后端一致，用于筛选下拉）
const CUSTOMER_NAMES = [...LOCAL_NAMED_CUSTOMERS, ...CROSS_NAMED_CUSTOMERS] as const;

// 规模看板 hub 展示顺序（按 sortingCode 匹配），如 LA（展示为 LAX）、EWR 等
const SCALE_HUB_ORDER = ['LA', 'EWR', 'ORD', 'ATL', 'MIA', 'DFW', 'HOU'];

// 分拣仓名称筛选项排除的 label
/** 普通订单下不展示的 Hub 子行（与 sortingCode 首列一致）；仅揽收分拣订单仍展示 */
const NORMAL_ORDER_HIDDEN_SUBHUB_SORTING_CODES = ['LAXSCBT', 'LAXSTEMU', 'LAXSFBT'] as const;

function isNormalOrderHiddenSubhubSortingCode(code: string | null | undefined): boolean {
    const c = (code ?? '').trim();
    return NORMAL_ORDER_HIDDEN_SUBHUB_SORTING_CODES.includes(c as (typeof NORMAL_ORDER_HIDDEN_SUBHUB_SORTING_CODES)[number]);
}

/**
 * 仅揽收分拣订单视图下需隐藏的「主 HUB」sortingCode（如 LA / EWR）。
 * 仅揽收分拣订单业务上只在揽收分拣子 HUB（LAXS / LAXSCBT / LAXSTEMU / LAXSFBT / EWRS）有数据，
 * 库里偶发主 HUB 上的脏数据应过滤掉。
 */
function isSortingOnlyHiddenMainHubSortingCode(code: string | null | undefined): boolean {
    const c = (code ?? '').trim();
    return SCALE_HUB_ORDER.includes(c);
}

/**
 * 仅揽收分拣模式下展示的固定客户白名单（与库内 customer_name 一致）。
 * 业务侧约定：仅揽收分拣只统计这 3 个客户的下单量/揽收量。
 * 注：ETL 写入时同一 customer_name 可能跨多个 customer_type（如 1-跨境专线 / 3-本地客户），
 *     需求是按客户名聚合，因此仅按 dbName 匹配，不限定 customer_type。
 */
const SORTING_ONLY_CUSTOMERS: { dbName: string; label: string }[] = [
    { dbName: 'TEMU-USPS', label: 'TEMU USPS' },
    { dbName: 'TikTok FBT', label: 'TikTok FBT' },
    { dbName: 'TikTok CBT', label: 'TikTok CBT' },
];

/**
 * 计算 avgLabel 列的值。
 * - 单期场景（isSinglePeriod=true）且有 data2 上期值时，返回上期值（T-7/上周/上月），
 *   避免与当期列同值、破坏「日均/周均/月均」语义。
 * - 否则按 sum/count 计算当期均值。
 */
function resolveAvgValue(
    currentPeriodSum: number,
    currentPeriodCount: number,
    isSinglePeriod: boolean,
    prevPeriodValue: number | null,
): number {
    // 单期场景下，「上上月 / 上上周 / 上周同日 / 上周同期」列就是对比期值；
    // 对比期某行无数据（prevPeriodValue=null）时显式归零，避免回退到当期均值，
    // 否则会把当期值错误地填进对比列（典型现象：仅揽收分拣 EWRS 上上月列显示当期值）。
    if (isSinglePeriod) {
        return prevPeriodValue ?? 0;
    }
    return currentPeriodCount > 0 ? currentPeriodSum / currentPeriodCount : 0;
}

// ==================== 规模看板下单趋势小时汇总行构造器 ====================

function computeHourlyChangeStr(curr: number, prev: number): string {
    if (curr === 0) return '-';
    if (prev === 0) return curr > 0 ? '+100.0%' : '0.0%';
    const change = ((curr - prev) / prev) * 100;
    return change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
}

function pivotHourlyItems(
    items: ScaleDashboardItem[],
    keyFn: (item: ScaleDashboardItem) => string,
    countFn: (item: ScaleDashboardItem) => number = (item) => Number(item.orderCount)
): Map<string, Record<string, number>> {
    const map = new Map<string, Record<string, number>>();
    items.forEach((item) => {
        const key = keyFn(item);
        if (!map.has(key)) map.set(key, {});
        const row = map.get(key)!;
        row[item.timePeriod] = (row[item.timePeriod] ?? 0) + countFn(item);
    });
    return map;
}

function sumPivotRow(row: Record<string, number>, periods: string[]): number {
    return periods.reduce((s, p) => s + (row[p] ?? 0), 0);
}

function computeHourlyChange(params: {
    currRow: Record<string, number>;
    prevRow: Record<string, number> | null;
    currPeriods: string[];
    prevPeriods: string[];
    queryType: ScaleQueryType;
    isSinglePeriod: boolean;
}): string {
    const { currRow, prevRow, currPeriods, prevPeriods, queryType, isSinglePeriod } = params;
    if (currPeriods.length === 0) return '-';
    const pLast = currPeriods[currPeriods.length - 1];
    const currVal = currRow[pLast] ?? 0;

    if (isSinglePeriod && prevRow != null && prevPeriods.length > 0) {
        const pPrev = prevPeriods[prevPeriods.length - 1];
        return computeHourlyChangeStr(currVal, prevRow[pPrev] ?? 0);
    }

    if (queryType === ScaleQueryType.DAILY && prevRow != null && prevPeriods.length > 0) {
        const parsedLast = safeParsePeriodInTz(pLast, DASHBOARD_PACIFIC_TZ);
        const tMinus7 = parsedLast ? parsedLast.subtract(7, 'day').format('YYYY-MM-DD') : '';
        if (tMinus7) {
            if (!currPeriods.includes(tMinus7)) {
                return computeHourlyChangeStr(currVal, prevRow[tMinus7] ?? 0);
            }
            return computeHourlyChangeStr(currVal, currRow[tMinus7] ?? 0);
        }
    }

    if (currPeriods.length >= 2) {
        return computeHourlyChangeStr(currVal, currRow[currPeriods[currPeriods.length - 2]] ?? 0);
    }
    return '-';
}

type HubInfoEntry = { id?: unknown; sortingCode?: string | null; name?: string | null };

function buildHubListFromItems(
    items: ScaleDashboardItem[],
    operableHubs: HubInfoEntry[],
    subTab: ScaleDashboardSubTab,
): { hubId: number; sortingCode: string }[] {
    const hubMap = new Map<number, string>();
    items.forEach((item) => {
        const hid = scaleItemHubId(item, subTab);
        if (hid > 0 && !hubMap.has(hid)) {
            const code = scaleItemSortingCode(item, subTab);
            if (code) {
                hubMap.set(hid, code);
            } else {
                const w = operableHubs.find((h) => Number(h.id) === hid);
                const label = ((w?.sortingCode ?? w?.name ?? '') as string).trim();
                hubMap.set(hid, label || `Hub-${hid}`);
            }
        }
    });
    return Array.from(hubMap.entries())
        .map(([hubId, sortingCode]) => ({ hubId, sortingCode }))
        .sort((a, b) => {
            const ia = SCALE_HUB_ORDER.findIndex((c) => a.sortingCode === c || a.sortingCode.startsWith(c));
            const ib = SCALE_HUB_ORDER.findIndex((c) => b.sortingCode === c || b.sortingCode.startsWith(c));
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });
}

function sumByFilter(
    pivot: Map<string, Record<string, number>>,
    keyFilter: (k: string) => boolean,
    periods: string[]
): Record<string, number> {
    const result: Record<string, number> = {};
    periods.forEach((p) => { result[p] = 0; });
    pivot.forEach((row, key) => {
        if (!keyFilter(key)) return;
        periods.forEach((p) => { result[p] = (result[p] ?? 0) + (row[p] ?? 0); });
    });
    return result;
}

function buildHourlySummaryCustomerRows(params: {
    items: ScaleDashboardItem[];
    items2: ScaleDashboardItem[];
    subTab: ScaleDashboardSubTab;
    /** 取数指标：order 取 orderCount；pickup 取 pickupCount。默认 order */
    metric?: 'order' | 'pickup';
    /** 1-揽收分拣+尾程（普通）；2-仅揽收分拣。仅普通模式下隐藏 LAXSCBT 等揽收分拣专用子 HUB。默认 1 */
    orderFlowType?: number;
    /**
     * 简化的客户类型视图（揽收下单监控 + 揽收分拣+尾程使用）：
     * - 命名客户行（Temu半托管 / TikTok CBT / ...）与其下的 HUB 子行 / 其他客户不渲染
     * - 「全美」行下追加按 HUB 拆分的子行
     * 默认 false（保留客户下单监控等场景的完整命名客户层级）。
     */
    simplifiedCustomerTypeView?: boolean;
    /**
     * 是否在「全美」行下追加按 HUB 拆分的子行（不分客户类型 / 客户名）。
     * - simplifiedCustomerTypeView=true 时强制 true（与原逻辑等价）
     * - 客户下单监控 / 揽收趋势（normal 模式）开启此项，可同时保留命名客户层级
     */
    expandNationHubs?: boolean;
    /**
     * 启用「其他客户」分组展开：
     * - 聚合行下展开显示具体非命名客户，每个具体客户下再展开 hub 拆分
     * - 该选项不影响其他场景（默认 false 时行为不变）
     */
    enableOtherCustomerExpansion?: boolean;
    periodColumns: string[];
    avgLabel: string;
    isSinglePeriod: boolean;
    queryType: ScaleQueryType;
    selectedHubId: number;
    singleAccessibleHubId: number | null;
    operableHubs: HubInfoEntry[];
    hasFullWarehouseAccess: boolean;
    selectedCustomerType: number | null;
    selectedCustomerName: string | null;
    nationMaxPeriods?: string[];
}): DataRow[] {
    const {
        items, items2, subTab, periodColumns, avgLabel, isSinglePeriod, queryType,
        selectedHubId, operableHubs, hasFullWarehouseAccess,
        selectedCustomerType, selectedCustomerName, nationMaxPeriods,
        metric = 'order', orderFlowType = 1, simplifiedCustomerTypeView = false,
        expandNationHubs = false, enableOtherCustomerExpansion = false,
    } = params;

    const countFn = metric === 'pickup'
        ? (i: ScaleDashboardItem) => Number(i.pickupCount)
        : (i: ScaleDashboardItem) => Number(i.orderCount);
    // 普通订单（揽收分拣+尾程）：隐藏揽收分拣专用子 HUB（LAXSCBT...）
    // 仅揽收分拣：隐藏主 HUB（LA/EWR...），只展示揽收分拣子 HUB（LAXS/LAXSCBT/LAXSTEMU/...）
    const shouldHideSubHub = (sortingCode: string): boolean => {
        if (orderFlowType === 1) return isNormalOrderHiddenSubhubSortingCode(sortingCode);
        if (orderFlowType === 2) return isSortingOnlyHiddenMainHubSortingCode(sortingCode);
        return false;
    };

    const rows: DataRow[] = [];
    const pivot1 = pivotHourlyItems(items, (i) => `${scaleItemHubId(i, subTab)}_${i.customerType ?? 'null'}_${i.customerName ?? ''}`, countFn);
    const pivot2 = pivotHourlyItems(items2, (i) => `${scaleItemHubId(i, subTab)}_${i.customerType ?? 'null'}_${i.customerName ?? ''}`, countFn);
    const pivot2Periods = Array.from(new Set(items2.map((i) => i.timePeriod))).sort();
    const hubListSorted = buildHubListFromItems(items, operableHubs, subTab);
    // 仅揽收分拣模式下需排除主 HUB（脏数据）：用于全美聚合 / 客户聚合时跳过这些 hubId
    const hiddenMainHubIdSet = new Set<string>(
        orderFlowType === 2
            ? hubListSorted
                .filter(({ sortingCode }) => isSortingOnlyHiddenMainHubSortingCode(sortingCode))
                .map(({ hubId }) => String(hubId))
            : []
    );
    const passHubFilter = (hubIdStr: string): boolean => !hiddenMainHubIdSet.has(hubIdStr);

    const makeChangeParams = (currRow: Record<string, number>, prevRow: Record<string, number> | null) => ({
        currRow, prevRow, currPeriods: periodColumns, prevPeriods: pivot2Periods, queryType, isSinglePeriod,
    });

    const makeDataRow = (key: string, label: string, indent: 0 | 1 | 2 | 3, headerBg: 'deep' | 'mid' | 'light', sumRow: Record<string, number>, prevRow: Record<string, number> | null): DataRow => {
        const periodSum = sumPivotRow(sumRow, periodColumns);
        const prevPeriodValue = isSinglePeriod && prevRow != null && pivot2Periods.length > 0
            ? Number(prevRow[pivot2Periods[pivot2Periods.length - 1]] ?? 0)
            : null;
        const avg = resolveAvgValue(periodSum, periodColumns.length, isSinglePeriod, prevPeriodValue);
        return {
            key, label, indent, headerBg,
            ...Object.fromEntries(periodColumns.map((p) => [p, sumRow[p] ?? 0])),
            [avgLabel]: avg,
            环比: computeHourlyChange(makeChangeParams(sumRow, prevRow)),
        };
    };

    // Nation row
    if (!selectedCustomerType && !selectedCustomerName) {
        const nationSum = sumByFilter(pivot1, (k) => Number(k.split('_')[0]) > 0 && passHubFilter(k.split('_')[0]), periodColumns);
        const nationPrev = sumByFilter(pivot2, (k) => Number(k.split('_')[0]) > 0 && passHubFilter(k.split('_')[0]), pivot2Periods);
        const nationRow: DataRow & { __nationMaxPeriods?: string[] } =
            makeDataRow('hourly_nation', '全美', 0, 'mid', nationSum, nationPrev);
        if (nationMaxPeriods && nationMaxPeriods.length > 0) {
            nationRow.__nationMaxPeriods = nationMaxPeriods;
        }
        rows.push(nationRow);

        // 全美下按 HUB 拆分（不分客户类型 / 客户名）
        //  - 简化视图：保留原条件（仅 selectedHubId=0 && hasFullWarehouseAccess 时展开）
        //  - 普通视图：由 expandNationHubs 控制，不再限制 selectedHubId（筛了具体 hub 时 hubListSorted 只剩该 hub）
        const shouldExpandNationHubs =
            (simplifiedCustomerTypeView && selectedHubId === 0 && hasFullWarehouseAccess) ||
            (expandNationHubs && hasFullWarehouseAccess);
        if (shouldExpandNationHubs) {
            hubListSorted.forEach(({ hubId, sortingCode }) => {
                if (shouldHideSubHub(sortingCode)) return;
                const hubFilter = (k: string) => Number(k.split('_')[0]) === hubId;
                const hubSum = sumByFilter(pivot1, hubFilter, periodColumns);
                const hubPrev = sumByFilter(pivot2, hubFilter, pivot2Periods);
                rows.push(makeDataRow(`hourly_nation_hub_${hubId}`, formatHubDisplayLabel(sortingCode), 1, 'mid', hubSum, hubPrev));
            });
        }
    }

    const buildCustomerSection = (ct: number, sectionLabel: string, namedCustomers: readonly string[]) => {
        if (selectedCustomerType != null && selectedCustomerType !== ct) return;
        const namedSet = new Set(namedCustomers as string[]);

        const ctFilter = (k: string) => k.split('_')[1] === String(ct) && Number(k.split('_')[0]) > 0 && passHubFilter(k.split('_')[0]);

        // Customer type header
        // 简化视图（揽收下单监控）下，命名客户层不渲染，customer type header 是唯一可用的渲染槽位；
        // 若仍按 !selectedCustomerName 屏蔽，会导致选客户名时整表为空。放宽后客户名只命中一个 ct，
        // 另一个 ct 的 sum 为 0，会被 filterRowsWithoutPeriodData 自动隐藏。
        const shouldRenderCtHeader = !selectedCustomerName || simplifiedCustomerTypeView;
        if (shouldRenderCtHeader) {
            const ctSum = sumByFilter(pivot1, ctFilter, periodColumns);
            const ctPrev = sumByFilter(pivot2, ctFilter, pivot2Periods);
            rows.push(makeDataRow(`hourly_ct_${ct}`, sectionLabel, 0, 'mid', ctSum, ctPrev));

            // Hub sub-rows for customer type (only when viewing all hubs)
            if (selectedHubId === 0 && hasFullWarehouseAccess) {
                hubListSorted.forEach(({ hubId, sortingCode }) => {
                    if (shouldHideSubHub(sortingCode)) return;
                    const hubCtFilter = (k: string) => Number(k.split('_')[0]) === hubId && k.split('_')[1] === String(ct);
                    const hubCtSum = sumByFilter(pivot1, hubCtFilter, periodColumns);
                    const hubCtPrev = sumByFilter(pivot2, hubCtFilter, pivot2Periods);
                    rows.push(makeDataRow(`hourly_ct_${ct}_hub_${hubId}`, formatHubDisplayLabel(sortingCode), 1, 'mid', hubCtSum, hubCtPrev));
                });
            }
        }

        // 简化视图：不渲染命名客户与「其他客户」层，只保留 customer type header + 其 HUB 子行
        if (simplifiedCustomerTypeView) {
            return;
        }

        // Named customers
        const visibleNamed = selectedCustomerName && selectedCustomerName !== '__other__'
            ? namedCustomers.filter((n) => n === selectedCustomerName)
            : namedCustomers;

        visibleNamed.forEach((cn) => {
            const cnFilter = (k: string) => {
                const parts = k.split('_');
                if (parts[1] !== String(ct)) return false;
                if (Number(parts[0]) === 0) return false;
                if (!passHubFilter(parts[0])) return false;
                return parts.slice(2).join('_') === cn;
            };
            const cnSum = sumByFilter(pivot1, cnFilter, periodColumns);
            const cnPrev = sumByFilter(pivot2, cnFilter, pivot2Periods);
            rows.push(makeDataRow(`hourly_cn_${ct}_${cn}`, cn, 1, 'light', cnSum, cnPrev));

            if (selectedHubId === 0 && hasFullWarehouseAccess && !selectedCustomerName) {
                hubListSorted.forEach(({ hubId, sortingCode }) => {
                    if (shouldHideSubHub(sortingCode)) return;
                    const hubCnKey = `${hubId}_${ct}_${cn}`;
                    const hubCnRow = pivot1.get(hubCnKey) ?? {};
                    const hubCnPrev = pivot2.get(hubCnKey) ?? null;
                    rows.push(makeDataRow(`hourly_cn_${ct}_${cn}_hub_${hubId}`, formatHubDisplayLabel(sortingCode), 2, 'light', hubCnRow, hubCnPrev));
                });
            }
        });

        // Other customers
        const otherAggregateKey = `hourly_cn_${ct}_other`;
        const otherFilter = (k: string) => {
            const parts = k.split('_');
            if (parts[1] !== String(ct)) return false;
            if (Number(parts[0]) === 0) return false;
            if (!passHubFilter(parts[0])) return false;
            const cn = parts.slice(2).join('_');
            return cn !== '' && !namedSet.has(cn);
        };
        const shouldShowOtherAggregate = !selectedCustomerName
            || selectedCustomerName === '__other__'
            || (enableOtherCustomerExpansion && !namedSet.has(selectedCustomerName));
        if (shouldShowOtherAggregate) {
            const otherSum = sumByFilter(pivot1, otherFilter, periodColumns);
            const otherPrev = sumByFilter(pivot2, otherFilter, pivot2Periods);
            const aggregateRow = makeDataRow(otherAggregateKey, '其他客户', 1, 'light', otherSum, otherPrev);
            if (enableOtherCustomerExpansion) {
                aggregateRow.expandable = true;
            }
            rows.push(aggregateRow);

            if (enableOtherCustomerExpansion) {
                // 收集当前 ct 下所有非命名客户名（按字典序）
                const otherCustomerNamesInCt = new Set<string>();
                pivot1.forEach((_, k) => {
                    if (otherFilter(k)) {
                        const parts = k.split('_');
                        otherCustomerNamesInCt.add(parts.slice(2).join('_'));
                    }
                });
                const sortedOthers = Array.from(otherCustomerNamesInCt).sort((a, b) => a.localeCompare(b, 'zh'));
                sortedOthers.forEach((cn) => {
                    const cnFilter = (k: string) => {
                        const parts = k.split('_');
                        if (parts[1] !== String(ct)) return false;
                        if (Number(parts[0]) === 0) return false;
                        if (!passHubFilter(parts[0])) return false;
                        return parts.slice(2).join('_') === cn;
                    };
                    const cnSum = sumByFilter(pivot1, cnFilter, periodColumns);
                    const cnPrev = sumByFilter(pivot2, cnFilter, pivot2Periods);
                    const cnKey = `${otherAggregateKey}_${cn}`;
                    const cnRow = makeDataRow(cnKey, cn, 2, 'light', cnSum, cnPrev);
                    cnRow.parentKey = otherAggregateKey;
                    rows.push(cnRow);
                });
            }
        }
    };

    // 选了具体非命名客户名时（脱敏展示）：表格仅一行「其他客户」，数据 = 该客户全部数据（不限 customer_type）
    //  - 不展开任何 customer_type section、不展示客户名本身
    //  - 后端已按 customerName 精确过滤，这里只需把数据汇总成「其他客户」行
    //  - 启用 enableOtherCustomerExpansion 时不走脱敏分支：让 buildCustomerSection 内输出完整层级（聚合行 + 具体客户 + hub）
    const isSpecificOtherCustomer = selectedCustomerName != null
        && selectedCustomerName !== '__other__'
        && !CUSTOMER_NAMES.includes(selectedCustomerName as (typeof CUSTOMER_NAMES)[number]);
    if (isSpecificOtherCustomer && !enableOtherCustomerExpansion) {
        const otherFilter = (k: string) => {
            const parts = k.split('_');
            if (Number(parts[0]) === 0) return false;
            if (!passHubFilter(parts[0])) return false;
            const cn = parts.slice(2).join('_');
            return cn === selectedCustomerName;
        };
        const otherSum = sumByFilter(pivot1, otherFilter, periodColumns);
        const otherPrev = sumByFilter(pivot2, otherFilter, pivot2Periods);
        rows.push(makeDataRow('hourly_cn_other_specific', '其他客户', 0, 'mid', otherSum, otherPrev));
        return rows;
    }

    buildCustomerSection(2, '本地揽收', LOCAL_NAMED_CUSTOMERS);
    buildCustomerSection(1, '跨境专线', CROSS_NAMED_CUSTOMERS);
    return rows;
}

/**
 * 仅揽收分拣模式专用：固定 3 个硬编码客户 × 所有目的地 HUB 两层结构。
 * - 全美汇总：仅这 3 个客户跨所有 HUB 求和（不含其它客户）。
 * - 每个客户：跨 HUB 求和；未选具体 HUB 且有全部仓权限时，下钻所有目的地 HUB 子行（不过滤主 HUB）。
 */
function buildSortingOnlyRowsCustomerByHub(params: {
    items: ScaleDashboardItem[];
    items2: ScaleDashboardItem[];
    subTab: ScaleDashboardSubTab;
    /** 取数指标：order 取 orderCount；pickup 取 pickupCount */
    metric: 'order' | 'pickup';
    periodColumns: string[];
    avgLabel: string;
    isSinglePeriod: boolean;
    queryType: ScaleQueryType;
    selectedHubId: number;
    operableHubs: HubInfoEntry[];
    hasFullWarehouseAccess: boolean;
    nationMaxPeriods?: string[];
}): DataRow[] {
    const {
        items, items2, subTab, metric, periodColumns, avgLabel, isSinglePeriod, queryType,
        selectedHubId, operableHubs, hasFullWarehouseAccess, nationMaxPeriods,
    } = params;
    const countFn = metric === 'pickup'
        ? (i: ScaleDashboardItem) => Number(i.pickupCount)
        : (i: ScaleDashboardItem) => Number(i.orderCount);

    const rows: DataRow[] = [];
    // 仅按 hubId + customerName 聚合（不再带 customerType，ETL 可能给同一客户名写多个 customer_type）
    const pivot1 = pivotHourlyItems(items, (i) => `${scaleItemHubId(i, subTab)}_${i.customerName ?? ''}`, countFn);
    const pivot2 = pivotHourlyItems(items2, (i) => `${scaleItemHubId(i, subTab)}_${i.customerName ?? ''}`, countFn);
    const pivot2Periods = Array.from(new Set(items2.map((i) => i.timePeriod))).sort();
    const hubListSorted = buildHubListFromItems(items, operableHubs, subTab);

    const allowedSet = new Set(SORTING_ONLY_CUSTOMERS.map((c) => c.dbName));
    const inAllowed = (k: string): boolean => {
        const parts = k.split('_');
        const hubIdStr = parts[0];
        if (Number(hubIdStr) <= 0) return false;
        const cn = parts.slice(1).join('_');
        return allowedSet.has(cn);
    };

    const makeChangeParams = (currRow: Record<string, number>, prevRow: Record<string, number> | null) => ({
        currRow, prevRow, currPeriods: periodColumns, prevPeriods: pivot2Periods, queryType, isSinglePeriod,
    });

    const makeDataRow = (key: string, label: string, indent: 0 | 1 | 2, headerBg: 'deep' | 'mid' | 'light', sumRow: Record<string, number>, prevRow: Record<string, number> | null): DataRow => {
        const periodSum = sumPivotRow(sumRow, periodColumns);
        const prevPeriodValue = isSinglePeriod && prevRow != null && pivot2Periods.length > 0
            ? Number(prevRow[pivot2Periods[pivot2Periods.length - 1]] ?? 0)
            : null;
        const avg = resolveAvgValue(periodSum, periodColumns.length, isSinglePeriod, prevPeriodValue);
        return {
            key, label, indent, headerBg,
            ...Object.fromEntries(periodColumns.map((p) => [p, sumRow[p] ?? 0])),
            [avgLabel]: avg,
            环比: computeHourlyChange(makeChangeParams(sumRow, prevRow)),
        };
    };

    // 全美：仅 3 个硬编码客户跨所有 HUB 求和
    const nationSum = sumByFilter(pivot1, inAllowed, periodColumns);
    const nationPrev = sumByFilter(pivot2, inAllowed, pivot2Periods);
    const nationRow: DataRow & { __nationMaxPeriods?: string[] } =
        makeDataRow('sorting_only_nation', '全美', 0, 'mid', nationSum, nationPrev);
    if (nationMaxPeriods && nationMaxPeriods.length > 0) {
        nationRow.__nationMaxPeriods = nationMaxPeriods;
    }
    rows.push(nationRow);

    SORTING_ONLY_CUSTOMERS.forEach(({ dbName, label }) => {
        const cnFilter = (k: string) => {
            const parts = k.split('_');
            if (Number(parts[0]) <= 0) return false;
            return parts.slice(1).join('_') === dbName;
        };
        const cnSum = sumByFilter(pivot1, cnFilter, periodColumns);
        const cnPrev = sumByFilter(pivot2, cnFilter, pivot2Periods);
        rows.push(makeDataRow(`sorting_only_cn_${dbName}`, label, 1, 'light', cnSum, cnPrev));

        if (selectedHubId === 0 && hasFullWarehouseAccess) {
            hubListSorted.forEach(({ hubId, sortingCode }) => {
                const hubCnKey = `${hubId}_${dbName}`;
                const hubCnRow = pivot1.get(hubCnKey) ?? {};
                const hubCnPrev = pivot2.get(hubCnKey) ?? null;
                rows.push(makeDataRow(`sorting_only_cn_${dbName}_hub_${hubId}`, formatHubDisplayLabel(sortingCode), 2, 'light', hubCnRow, hubCnPrev));
            });
        }
    });

    return rows;
}

function buildHourlySummaryPickupSortingOnlyRows(params: {
    items: ScaleDashboardItem[];
    items2: ScaleDashboardItem[];
    /** 取数指标：order 取 orderCount；pickup 取 pickupCount。默认 order */
    metric?: 'order' | 'pickup';
    periodColumns: string[];
    avgLabel: string;
    isSinglePeriod: boolean;
    queryType: ScaleQueryType;
    operableHubs: HubInfoEntry[];
    nationMaxPeriods?: string[];
}): DataRow[] {
    const { items, items2, periodColumns, avgLabel, isSinglePeriod, queryType, operableHubs, nationMaxPeriods, metric = 'order' } = params;
    const countFn = metric === 'pickup'
        ? (i: ScaleDashboardItem) => Number(i.pickupCount)
        : (i: ScaleDashboardItem) => Number(i.orderCount);
    const rows: DataRow[] = [];
    const pivot1 = pivotHourlyItems(items, (i) => `${scaleItemHubId(i, ScaleDashboardSubTab.PICKUP_ORDER)}`, countFn);
    const pivot2 = pivotHourlyItems(items2, (i) => `${scaleItemHubId(i, ScaleDashboardSubTab.PICKUP_ORDER)}`, countFn);
    const pivot2Periods = Array.from(new Set(items2.map((i) => i.timePeriod))).sort();
    const hubListAll = buildHubListFromItems(items, operableHubs, ScaleDashboardSubTab.PICKUP_ORDER);
    // 仅揽收分拣视图：过滤掉主 HUB（LA/EWR...），只展示揽收分拣子 HUB（LAXS/LAXSCBT/...）
    const hubListSorted = hubListAll.filter(({ sortingCode }) => !isSortingOnlyHiddenMainHubSortingCode(sortingCode));
    const hiddenMainHubIdSet = new Set<string>(
        hubListAll
            .filter(({ sortingCode }) => isSortingOnlyHiddenMainHubSortingCode(sortingCode))
            .map(({ hubId }) => String(hubId))
    );

    const makeChangeParams = (currRow: Record<string, number>, prevRow: Record<string, number> | null) => ({
        currRow, prevRow, currPeriods: periodColumns, prevPeriods: pivot2Periods, queryType, isSinglePeriod,
    });

    const makeDataRow = (key: string, label: string, indent: 0 | 1, headerBg: 'mid' | 'light', sumRow: Record<string, number>, prevRow: Record<string, number> | null): DataRow => {
        const periodSum = sumPivotRow(sumRow, periodColumns);
        const prevPeriodValue = isSinglePeriod && prevRow != null && pivot2Periods.length > 0
            ? Number(prevRow[pivot2Periods[pivot2Periods.length - 1]] ?? 0)
            : null;
        const avg = resolveAvgValue(periodSum, periodColumns.length, isSinglePeriod, prevPeriodValue);
        return {
            key, label, indent, headerBg,
            ...Object.fromEntries(periodColumns.map((p) => [p, sumRow[p] ?? 0])),
            [avgLabel]: avg,
            环比: computeHourlyChange(makeChangeParams(sumRow, prevRow)),
        };
    };

    const nationSum = sumByFilter(pivot1, (k) => Number(k) > 0 && !hiddenMainHubIdSet.has(k), periodColumns);
    const nationPrev = sumByFilter(pivot2, (k) => Number(k) > 0 && !hiddenMainHubIdSet.has(k), pivot2Periods);
    const nationRow: DataRow & { __nationMaxPeriods?: string[] } =
        makeDataRow('hourly_sorting_nation', '全美', 0, 'mid', nationSum, nationPrev);
    if (nationMaxPeriods && nationMaxPeriods.length > 0) {
        nationRow.__nationMaxPeriods = nationMaxPeriods;
    }
    rows.push(nationRow);

    hubListSorted.forEach(({ hubId, sortingCode }) => {
        const hubRow = pivot1.get(String(hubId)) ?? {};
        const hubPrev = pivot2.get(String(hubId)) ?? null;
        rows.push(makeDataRow(`hourly_sorting_hub_${hubId}`, formatHubDisplayLabel(sortingCode), 1, 'light', hubRow, hubPrev));
    });

    return rows;
}

function buildHourlySummaryLastMileRows(params: {
    items: ScaleDashboardItem[];
    items2: ScaleDashboardItem[];
    metric: 'order' | 'pickup';
    periodColumns: string[];
    avgLabel: string;
    isSinglePeriod: boolean;
    queryType: ScaleQueryType;
    operableHubs: HubInfoEntry[];
    nationMaxPeriods?: string[];
}): DataRow[] {
    const { items, items2, metric, periodColumns, avgLabel, isSinglePeriod, queryType, operableHubs, nationMaxPeriods } = params;
    const rows: DataRow[] = [];
    const countFn = (i: ScaleDashboardItem) => (metric === 'pickup' ? Number(i.pickupCount) : Number(i.orderCount));
    const pivot1 = pivotHourlyItems(items, (i) => `${i.destHubId ?? 0}_${i.destWarehouseId ?? 'null'}`, countFn);
    const pivot2 = pivotHourlyItems(items2, (i) => `${i.destHubId ?? 0}_${i.destWarehouseId ?? 'null'}`, countFn);
    const pivot2Periods = Array.from(new Set(items2.map((i) => i.timePeriod))).sort();
    const hubListSorted = buildHubListFromItems(items, operableHubs, ScaleDashboardSubTab.LAST_MILE_ORDER);

    const warehouseMap = new Map<number, Map<number, string>>();
    items.forEach((item) => {
        const hid = Number(item.destHubId ?? 0);
        const wid = item.destWarehouseId != null ? Number(item.destWarehouseId) : null;
        if (hid > 0 && wid != null) {
            if (!warehouseMap.has(hid)) warehouseMap.set(hid, new Map());
            const wm = warehouseMap.get(hid)!;
            if (!wm.has(wid)) wm.set(wid, (item.destWarehouseSortingCode ?? `WHS-${wid}`).trim());
        }
    });

    const makeChangeParams = (currRow: Record<string, number>, prevRow: Record<string, number> | null) => ({
        currRow, prevRow, currPeriods: periodColumns, prevPeriods: pivot2Periods, queryType, isSinglePeriod,
    });

    const makeDataRow = (key: string, label: string, indent: 0 | 1, headerBg: 'mid' | 'light', sumRow: Record<string, number>, prevRow: Record<string, number> | null): DataRow => {
        const periodSum = sumPivotRow(sumRow, periodColumns);
        const prevPeriodValue = isSinglePeriod && prevRow != null && pivot2Periods.length > 0
            ? Number(prevRow[pivot2Periods[pivot2Periods.length - 1]] ?? 0)
            : null;
        const avg = resolveAvgValue(periodSum, periodColumns.length, isSinglePeriod, prevPeriodValue);
        return {
            key, label, indent, headerBg,
            ...Object.fromEntries(periodColumns.map((p) => [p, sumRow[p] ?? 0])),
            [avgLabel]: avg,
            环比: computeHourlyChange(makeChangeParams(sumRow, prevRow)),
        };
    };

    // Nation row: sum all leaf entries (hubId > 0, warehouseId != null)
    const nationLeafFilter = (k: string) => {
        const parts = k.split('_');
        return Number(parts[0]) > 0 && parts[1] !== 'null';
    };
    const nationSum = sumByFilter(pivot1, nationLeafFilter, periodColumns);
    const nationPrev = sumByFilter(pivot2, nationLeafFilter, pivot2Periods);
    const nationRow: DataRow & { __nationMaxPeriods?: string[] } =
        makeDataRow('lastmile_nation', '全美', 0, 'mid', nationSum, nationPrev);
    if (nationMaxPeriods && nationMaxPeriods.length > 0) {
        nationRow.__nationMaxPeriods = nationMaxPeriods;
    }
    rows.push(nationRow);

    hubListSorted.forEach(({ hubId, sortingCode }) => {
        const hubLeafFilter = (k: string) => {
            const parts = k.split('_');
            return Number(parts[0]) === hubId && parts[1] !== 'null';
        };
        const hubSum = sumByFilter(pivot1, hubLeafFilter, periodColumns);
        const hubPrev = sumByFilter(pivot2, hubLeafFilter, pivot2Periods);
        rows.push(makeDataRow(`lastmile_hub_${hubId}`, formatHubDisplayLabel(sortingCode), 0, 'mid', hubSum, hubPrev));

        const whMap = warehouseMap.get(hubId);
        if (whMap) {
            const sortedWarehouses = Array.from(whMap.entries()).sort(([, a], [, b]) => a.localeCompare(b));
            sortedWarehouses.forEach(([wid, whCode]) => {
                const leafKey = `${hubId}_${wid}`;
                const leafRow = pivot1.get(leafKey) ?? {};
                const leafPrev = pivot2.get(leafKey) ?? null;
                rows.push(makeDataRow(`lastmile_hub_${hubId}_whs_${wid}`, whCode || `WHS-${wid}`, 1, 'light', leafRow, leafPrev));
            });
        }
    });

    return rows;
}

// 「全美」聚合行 key 白名单：即使本周期内所有日期为 0 也保留展示
const NATION_ROW_KEYS = new Set<string>([
    'hourly_nation',
    'hourly_sorting_nation',
    'lastmile_nation',
    'sorting_only_nation',
]);

/**
 * 过滤掉本周期内（所有 period 列）数据全为 0 的行。
 * - 全美行始终保留
 * - 其他行（hub、客户类型、命名客户、whs 等）若所有 period 列均为 0/空则隐藏
 *   - 父子行均为 sum 关系，父行为 0 时其所有子行也为 0，会被一并过滤
 */
function filterRowsWithoutPeriodData(rows: DataRow[], periodColumns: string[]): DataRow[] {
    return rows.filter((row) => {
        if (NATION_ROW_KEYS.has(row.key)) return true;
        return periodColumns.some((p) => {
            const v = Number((row as Record<string, unknown>)[p]);
            return Number.isFinite(v) && v !== 0;
        });
    });
}

/**
 * 根据展开状态过滤树形扁平 rows：
 * - 没有 parentKey 的行始终可见
 * - 有 parentKey 的行：祖先链上每一层都必须在 expandedKeys 内才可见
 */
function filterByExpansion(rows: DataRow[], expandedKeys: Set<string>): DataRow[] {
    const rowByKey = new Map(rows.map((r) => [r.key, r]));
    const isVisible = (row: DataRow): boolean => {
        if (!row.parentKey) return true;
        if (!expandedKeys.has(row.parentKey)) return false;
        const parent = rowByKey.get(row.parentKey);
        if (!parent) return true;
        return isVisible(parent);
    };
    return rows.filter(isVisible);
}

/**
 * 看板指标注释 dateKey：DAILY 期本身即具体日期；WEEKLY 期已是 'start~end' 原样透传；
 * MONTHLY 裸月份（'YYYY-MM'）展开为当月起止日期，对齐 plan 约定的 RAW dateKey 格式。
 */
function computeScaleAnnotationDateKey(period: string, queryType: ScaleQueryType): string {
    if (queryType === ScaleQueryType.DAILY || period.includes('~')) {
        return period;
    }
    const month = dayjs(period, 'YYYY-MM', true);
    if (month.isValid()) {
        return `${month.startOf('month').format('YYYY-MM-DD')}~${month.endOf('month').format('YYYY-MM-DD')}`;
    }
    return period;
}

const scaleAnnotationConfig = dashboardAnnotationRegistry[PAGE_KEY_SCALE];

const ScaleDataPage: React.FC = () => {
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
    const [selectedHubId, setSelectedHubId] = useState<number>(0);
    const [selectedCustomerType, setSelectedCustomerType] = useState<number | null>(null);
    const [selectedCustomerName, setSelectedCustomerName] = useState<string | null>(null);
    // 累积"已发现的非命名客户名 + 其 customerType"。筛选导致 rawList/hourlyItems 缩窄时，
    // 已发现的客户名仍保留在下拉里，避免选项突然消失。带 customerType 用于客户类型↔客户名称的级联过滤。
    type DiscoveredOtherCustomer = { name: string; customerType: number };
    const [discoveredOtherCustomers, setDiscoveredOtherCustomers] = useState<DiscoveredOtherCustomer[]>([]);
    // 「其他客户」分组的展开/收起状态：默认全部收起；包含「其他客户聚合行 key」和「具体客户行 key」
    const [expandedOtherKeys, setExpandedOtherKeys] = useState<Set<string>>(new Set());
    const [scaleTab, setScaleTab] = useState<'order' | 'pickup'>('order');
    const [orderSubTab, setOrderSubTab] = useState<'customer' | 'pickup' | 'lastMile'>('customer');
    // 尾程下单监控：按某个日期列对 whs 行排序（hub 行顺序不变，全美行固定第一）
    const [lastMileSort, setLastMileSort] = useState<{ period: string; order: 'descend' | 'ascend' } | null>(null);
    const [orderFlowTabCustomerOrder, setOrderFlowTabCustomerOrder] = useState<'normal' | 'sorting'>('normal');
    const [orderFlowTabPickupOrder, setOrderFlowTabPickupOrder] = useState<'normal' | 'sorting'>('normal');
    const [orderFlowTabPickup, setOrderFlowTabPickup] = useState<'normal' | 'sorting'>('normal');
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [generateDashboardTableExport] = useMutation<
        GenerateDashboardTableExportResponse,
        GenerateDashboardTableExportVariables
    >(GENERATE_DASHBOARD_TABLE_EXPORT);

    /** 仅揽收分拣下客户筛选不可用，切到该模式时清空客户类型/名称；分拣仓保持用户当前选择 */
    const resetCustomerFiltersWhenEnterSortingFlow = useCallback(() => {
        setSelectedCustomerType(null);
        setSelectedCustomerName(null);
    }, []);

    const handleOrderFlowTabCustomerOrderChange = useCallback(
        (v: 'normal' | 'sorting') => {
            setOrderFlowTabCustomerOrder(v);
            if (v === 'sorting') {
                resetCustomerFiltersWhenEnterSortingFlow();
            }
        },
        [resetCustomerFiltersWhenEnterSortingFlow]
    );

    const handleOrderFlowTabPickupOrderChange = useCallback(
        (v: 'normal' | 'sorting') => {
            setOrderFlowTabPickupOrder(v);
            if (v === 'sorting') {
                resetCustomerFiltersWhenEnterSortingFlow();
            }
        },
        [resetCustomerFiltersWhenEnterSortingFlow]
    );

    const handleOrderFlowTabPickupChange = useCallback(
        (v: 'normal' | 'sorting') => {
            setOrderFlowTabPickup(v);
            if (v === 'sorting') {
                resetCustomerFiltersWhenEnterSortingFlow();
            }
        },
        [resetCustomerFiltersWhenEnterSortingFlow]
    );

    // 当前可见 tab 对应的「订单类型」状态。下单趋势按 subTab 区分；揽收趋势走 orderFlowTabPickup。
    // 修复历史 bug：原实现固定读 orderFlowTabPickup，导致从揽收趋势切回下单趋势后客户筛选仍被错误禁用。
    const orderFlowForActiveTab: 'normal' | 'sorting' = scaleTab === 'order'
        ? (orderSubTab === 'customer'
            ? orderFlowTabCustomerOrder
            : orderSubTab === 'pickup'
                ? orderFlowTabPickupOrder
                : 'normal')
        : orderFlowTabPickup;
    const orderFlowTypeParam =
        orderFlowForActiveTab === 'sorting' ? OrderFlowType.SORTING_ONLY : OrderFlowType.NORMAL;
    const isCustomerFiltered = selectedCustomerType != null || selectedCustomerName != null;

    const hourlyOrderFlowType =
        (orderSubTab === 'customer' && orderFlowTabCustomerOrder === 'sorting') ||
        (orderSubTab === 'pickup' && orderFlowTabPickupOrder === 'sorting')
            ? OrderFlowType.SORTING_ONLY : OrderFlowType.NORMAL;
    // 历史最高皇冠跟随当前可见 tab 的订单类型，避免在 hourly tab 仅分拣视图下用 pickup tab 的 NORMAL 取错日期
    const activeOrderFlowType = scaleTab === 'order' ? hourlyOrderFlowType : orderFlowTypeParam;

    const { hubOptions, loading: hubsLoading, singleAccessibleHubId, operableHubs, hasFullWarehouseAccess } =
        useOperableHubSelectOptions({
            excludeLabels: [...DASHBOARD_EXCLUDE_HUB_LABELS],
        });

    const { data, loading } = useQuery<QueryScaleDashboardResponse>(
        QUERY_SCALE_DASHBOARD,
        {
            variables: {
                input: {
                    queryType: timeRange?.queryType,
                    startDate: timeRange?.startDate,
                    endDate: timeRange?.endDate,
                    startHour: timeRange?.startHour ?? undefined,
                    endHour: timeRange?.endHour ?? undefined,
                    hubId: selectedHubId,
                    subTab: ScaleDashboardSubTab.PICKUP_TREND,
                    customerType: effectiveCustomerTypeForQuery(selectedCustomerType),
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    orderFlowType: orderFlowTypeParam,
                } as ScaleDashboardQueryInput,
            },
            skip: !timeRange || scaleTab !== 'pickup',
            onError: (error) => {
                console.error('查询规模数据失败:', error);
                message.error('查询规模数据失败，请稍后重试');
            },
        }
    );

    const rawList = (data?.queryScaleDashboard?.items ?? []).map(normalizeScaleItem);

    // 昨日/上周/上月或自定义范围不足7天/2周/2月时请求 data2；日始终请求，周不足2周、月不足2月也请求；多期时 data2 基于 data1 最后有数据周期
    const { isSinglePeriod, twoPeriodTimeRange } = useMemo(() => {
        if (!timeRange) return { isSinglePeriod: false, twoPeriodTimeRange: null as TimeRange | null };
        const start = safeParsePeriodInTz(timeRange.startDate, DASHBOARD_PACIFIC_TZ);
        const end = safeParsePeriodInTz(timeRange.endDate, DASHBOARD_PACIFIC_TZ);
        if (!start || !end) return { isSinglePeriod: false, twoPeriodTimeRange: null };
        const qt = timeRange.queryType;
        const daysInRange = end.diff(start, 'day') + 1;
        const monthsInRange = (end.year() - start.year()) * 12 + (end.month() - start.month()) + 1;

        const isSingle =
            (qt === ScaleQueryType.DAILY && start.isSame(end, 'day')) ||
            (qt === ScaleQueryType.WEEKLY && daysInRange <= 7) ||
            (qt === ScaleQueryType.MONTHLY && start.isSame(end, 'month'));

        // 日始终请求 data2；周不足 2 周、月不足 2 月也请求（含自定义时间范围）
        const needTwoPeriods =
            isSingle ||
            qt === ScaleQueryType.DAILY ||
            (qt === ScaleQueryType.WEEKLY && daysInRange < 14) ||
            (qt === ScaleQueryType.MONTHLY && monthsInRange < 2);

        if (!needTwoPeriods) return { isSinglePeriod: false, twoPeriodTimeRange: null };

        const avgLabels = ['日均', '周均', '月均'];
        const periodsFromData1 = Array.from(new Set(rawList.map((item: ScaleDashboardItem) => item.timePeriod)))
            .filter((p: string) => !avgLabels.includes(p))
            .sort();

        let start2: string;
        let end2: string;

        if (qt === ScaleQueryType.DAILY) {
            // 日粒度周环比 =（时间范围内最近有数的一天）vs（该日 T-7）；data2 只请求该 T-7 单日
            if (periodsFromData1.length === 0) return { isSinglePeriod: false, twoPeriodTimeRange: null };
            const lastDayWithData = periodsFromData1[periodsFromData1.length - 1];
            const parsedDay = safeParsePeriodInTz(lastDayWithData, DASHBOARD_PACIFIC_TZ);
            const tMinus7 = parsedDay
                ? parsedDay.subtract(7, 'day').format('YYYY-MM-DD')
                : start.subtract(7, 'day').format('YYYY-MM-DD');
            start2 = tMinus7;
            end2 = tMinus7;
            if (timeRange.todayRealtime) {
                // data1 endHour 传的是「用户当前美西整点」，但 ETL 整点结束后才写入，当前小时表里还没有行；
                // 若把同一 endHour 透传给 data2（T-7 已是完整历史），data2 会拉多一小时数据 → 环比偏低。
                // 从 dataUpdatedAt 反推「表里实际覆盖到的最后一小时」= updatedAt.hour() - 1，两边口径就对齐。
                // 跨天场景（updatedAt 落在 0 点）反推得 -1，说明当天无完整小时可对比，不请求 data2，环比自然显示 -。
                // dataUpdatedAt 为「美西 wall-clock 字符串（无时区后缀）」，必须显式按美西解析，
                // 否则 dayjs 会按浏览器本地时区兜底（如 UTC+8）导致 .hour() 偏移。
                const dataUpdatedAt = data?.queryScaleDashboard?.dataUpdatedAt;
                const alignedEndHour = dataUpdatedAt
                    ? dayjs.tz(dataUpdatedAt, DASHBOARD_PACIFIC_TZ).hour() - 1
                    : null;
                if (alignedEndHour == null || alignedEndHour < 0) {
                    return { isSinglePeriod: isSingle, twoPeriodTimeRange: null };
                }
                return {
                    isSinglePeriod: isSingle,
                    twoPeriodTimeRange: {
                        queryType: qt,
                        startDate: start2,
                        endDate: end2,
                        startHour: timeRange.startHour,
                        endHour: alignedEndHour,
                    },
                };
            }
        } else if (qt === ScaleQueryType.WEEKLY) {
            if (isSingle) {
                const prevWeekStart = start.subtract(1, 'week').startOf('isoWeek');
                start2 = prevWeekStart.format('YYYY-MM-DD');
                end2 = prevWeekStart.endOf('isoWeek').format('YYYY-MM-DD');
            } else {
                if (periodsFromData1.length === 0) return { isSinglePeriod: false, twoPeriodTimeRange: null };
                const lastPeriodWithData = periodsFromData1[periodsFromData1.length - 1];
                const parsed = safeParsePeriodInTz(lastPeriodWithData, DASHBOARD_PACIFIC_TZ);
                if (!parsed) {
                    start2 = start.subtract(daysInRange, 'day').format('YYYY-MM-DD');
                    end2 = start.subtract(1, 'day').format('YYYY-MM-DD');
                } else {
                    const lastWeekStart = parsed.startOf('isoWeek');
                    const prevWeekStart = lastWeekStart.subtract(1, 'week');
                    start2 = prevWeekStart.format('YYYY-MM-DD');
                    end2 = prevWeekStart.endOf('isoWeek').format('YYYY-MM-DD');
                }
            }
        } else {
            if (isSingle) {
                const prevMonthStart = start.subtract(1, 'month').startOf('month');
                start2 = prevMonthStart.format('YYYY-MM-DD');
                end2 = prevMonthStart.endOf('month').format('YYYY-MM-DD');
            } else {
                if (periodsFromData1.length === 0) return { isSinglePeriod: false, twoPeriodTimeRange: null };
                const lastPeriodWithData = periodsFromData1[periodsFromData1.length - 1];
                const monthStr = /^\d{4}-\d{2}$/.test(lastPeriodWithData) ? `${lastPeriodWithData}-01` : lastPeriodWithData;
                const parsed = safeParsePeriodInTz(monthStr, DASHBOARD_PACIFIC_TZ);
                if (!parsed) {
                    start2 = start.subtract(monthsInRange, 'month').startOf('month').format('YYYY-MM-DD');
                    end2 = start.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
                } else {
                    const lastMonthStart = parsed.startOf('month');
                    const prevMonthStart = lastMonthStart.subtract(1, 'month');
                    start2 = prevMonthStart.format('YYYY-MM-DD');
                    end2 = prevMonthStart.endOf('month').format('YYYY-MM-DD');
                }
            }
        }
        // 透传用户选择的小时窗：DAILY 时 timeRange 会带 startHour/endHour（默认 0/23 或用户 picker 值），
        // 保证 data2 的小时窗与 data1 对齐；WEEKLY/MONTHLY 时是 undefined，等价原整天行为。
        return {
            isSinglePeriod: isSingle,
            twoPeriodTimeRange: {
                queryType: qt,
                startDate: start2,
                endDate: end2,
                startHour: timeRange.startHour,
                endHour: timeRange.endHour,
            },
        };
    }, [timeRange, rawList]);

    const { data: data2 } = useQuery<QueryScaleDashboardResponse>(
        QUERY_SCALE_DASHBOARD,
        {
            variables: {
                input: {
                    queryType: twoPeriodTimeRange?.queryType,
                    startDate: twoPeriodTimeRange?.startDate,
                    endDate: twoPeriodTimeRange?.endDate,
                    startHour: twoPeriodTimeRange?.startHour ?? undefined,
                    endHour: twoPeriodTimeRange?.endHour ?? undefined,
                    hubId: selectedHubId,
                    subTab: ScaleDashboardSubTab.PICKUP_TREND,
                    customerType: effectiveCustomerTypeForQuery(selectedCustomerType),
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    orderFlowType: orderFlowTypeParam,
                } as ScaleDashboardQueryInput,
            },
            skip: !twoPeriodTimeRange || scaleTab !== 'pickup',
        }
    );

    // 历史最高值（用于皇冠）：独立接口，最近 90 天到当前时间（按美西时区，前后端一致）
    // 后端同样按 90 天收敛，此处传更早的日期也不会生效
    const nowInPacific = dayjs().tz(DASHBOARD_PACIFIC_TZ);
    const historicalMaxStartDate = nowInPacific.subtract(HISTORICAL_MAX_LOOKBACK_DAYS, 'day').format('YYYY-MM-DD');
    const historicalMaxEndDate = nowInPacific.format('YYYY-MM-DD');
    const historicalMetric = scaleTab === 'order' ? ScaleDashboardMetric.ORDER : ScaleDashboardMetric.PICKUP;
    const historicalMaxSubTab = scaleTab === 'order'
        ? (orderSubTab === 'customer'
            ? ScaleDashboardSubTab.CUSTOMER_ORDER
            : orderSubTab === 'pickup'
                ? ScaleDashboardSubTab.PICKUP_ORDER
                : ScaleDashboardSubTab.LAST_MILE_ORDER)
        : ScaleDashboardSubTab.PICKUP_TREND;

    const { data: historicalMaxData } = useQuery<QueryScaleDashboardHistoricalMaxResponse>(
        QUERY_SCALE_DASHBOARD_HISTORICAL_MAX,
        {
            variables: {
                input: {
                    queryType: timeRange?.queryType,
                    startDate: historicalMaxStartDate,
                    endDate: historicalMaxEndDate,
                    hubId: selectedHubId,
                    customerType: effectiveCustomerTypeForQuery(selectedCustomerType),
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    metric: historicalMetric,
                    orderFlowType: activeOrderFlowType,
                    subTab: historicalMaxSubTab,
                } as ScaleDashboardHistoricalMaxQueryInput,
            },
            // 仅在未筛选客户时请求（与普通订单全美皇冠一致）；仅揽收分拣的全美行也展示皇冠
            skip: !timeRange || selectedCustomerType != null || selectedCustomerName != null,
        }
    );

    const handleTimeRangeChange = useCallback((newTimeRange: TimeRange) => {
        setTimeRange(newTimeRange);
    }, []);

    // ===== 规模看板下单趋势小时汇总查询 =====
    const hourlySubTab: ScaleDashboardSubTab =
        orderSubTab === 'customer' ? ScaleDashboardSubTab.CUSTOMER_ORDER :
        orderSubTab === 'pickup' ? ScaleDashboardSubTab.PICKUP_ORDER :
        ScaleDashboardSubTab.LAST_MILE_ORDER;

    const { data: hourlyData, loading: hourlyLoading } = useQuery<QueryScaleDashboardResponse>(
        QUERY_SCALE_DASHBOARD,
        {
            variables: {
                input: {
                    queryType: timeRange?.queryType,
                    startDate: timeRange?.startDate,
                    endDate: timeRange?.endDate,
                    startHour: timeRange?.startHour ?? null,
                    endHour: timeRange?.endHour ?? null,
                    hubId: selectedHubId,
                    subTab: hourlySubTab,
                    customerType: effectiveCustomerTypeForQuery(selectedCustomerType),
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    orderFlowType: hourlyOrderFlowType,
                } as ScaleDashboardQueryInput,
            },
            skip: !timeRange || scaleTab !== 'order',
            onError: (error) => {
                console.error('查询规模小时汇总数据失败:', error);
                message.error('查询规模小时汇总数据失败，请稍后重试');
            },
        }
    );

    const twoPeriodTimeRangeForHourly = useMemo((): TimeRange | null => {
        if (!timeRange) return null;
        const qt = timeRange.queryType;
        const endD = safeParsePeriodInTz(timeRange.endDate, DASHBOARD_PACIFIC_TZ);
        const startD = safeParsePeriodInTz(timeRange.startDate, DASHBOARD_PACIFIC_TZ);
        if (!startD || !endD) return null;
        if (timeRange.todayRealtime) {
            // bug 1：today.hour() 会拉多一小时（ETL 未写入当前小时），从 dataUpdatedAt 反推到最后一个完整小时。
            // dataUpdatedAt 为「美西 wall-clock 字符串（无时区后缀）」，必须显式按美西解析。
            const dataUpdatedAt = hourlyData?.queryScaleDashboard?.dataUpdatedAt;
            const alignedEndHour = dataUpdatedAt
                ? dayjs.tz(dataUpdatedAt, DASHBOARD_PACIFIC_TZ).hour() - 1
                : null;
            if (alignedEndHour == null || alignedEndHour < 0) return null;
            const t7 = endD.subtract(7, 'day').format('YYYY-MM-DD');
            return { queryType: ScaleQueryType.DAILY, startDate: t7, endDate: t7, startHour: timeRange.startHour, endHour: alignedEndHour };
        }
        if (qt === ScaleQueryType.DAILY) {
            const t7 = endD.subtract(7, 'day').format('YYYY-MM-DD');
            // bug 2：透传用户小时窗，与 data1 对齐；默认 0/23 时等价整天
            return { queryType: qt, startDate: t7, endDate: t7, startHour: timeRange.startHour, endHour: timeRange.endHour };
        }
        if (qt === ScaleQueryType.WEEKLY) {
            const prevStart = startD.subtract(1, 'week').startOf('isoWeek');
            return { queryType: qt, startDate: prevStart.format('YYYY-MM-DD'), endDate: prevStart.endOf('isoWeek').format('YYYY-MM-DD') };
        }
        if (qt === ScaleQueryType.MONTHLY) {
            const prevStart = startD.subtract(1, 'month').startOf('month');
            return { queryType: qt, startDate: prevStart.format('YYYY-MM-DD'), endDate: prevStart.endOf('month').format('YYYY-MM-DD') };
        }
        return null;
    }, [timeRange, hourlyData]);

    const { data: hourlyData2 } = useQuery<QueryScaleDashboardResponse>(
        QUERY_SCALE_DASHBOARD,
        {
            variables: {
                input: {
                    queryType: twoPeriodTimeRangeForHourly?.queryType,
                    startDate: twoPeriodTimeRangeForHourly?.startDate,
                    endDate: twoPeriodTimeRangeForHourly?.endDate,
                    startHour: twoPeriodTimeRangeForHourly?.startHour ?? null,
                    endHour: twoPeriodTimeRangeForHourly?.endHour ?? null,
                    hubId: selectedHubId,
                    subTab: hourlySubTab,
                    customerType: effectiveCustomerTypeForQuery(selectedCustomerType),
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    orderFlowType: hourlyOrderFlowType,
                } as ScaleDashboardQueryInput,
            },
            skip: !twoPeriodTimeRangeForHourly || scaleTab !== 'order',
        }
    );

    const hourlyItems: ScaleDashboardItem[] = (hourlyData?.queryScaleDashboard?.items ?? []).map(normalizeScaleItem);
    const hourlyItems2: ScaleDashboardItem[] = (hourlyData2?.queryScaleDashboard?.items ?? []).map(normalizeScaleItem);
    const hourlyDataUpdatedAt: string | null = hourlyData?.queryScaleDashboard?.dataUpdatedAt ?? null;
    const pickupDataUpdatedAt: string | null = data?.queryScaleDashboard?.dataUpdatedAt ?? null;

    const orderSubTabNote = useMemo(() => {
        if (orderSubTab === 'customer') return '按客户 x 目的地HUB拆解下单量，用于监控客户下单规模是否异常。';
        if (orderSubTab === 'pickup') return '按应揽收HUB拆解下单量，用于提前规划各HUB分拣运力。';
        return '按目的地HUB x Warehouse拆解下单量，固定”揽收分拣+尾程”，用于提前规划尾程运力。';
    }, [orderSubTab]);

    useEffect(() => {
        if (singleAccessibleHubId != null) {
            setSelectedHubId(singleAccessibleHubId);
        }
    }, [singleAccessibleHubId]);

    // 收集 rawList / hourlyItems 中所有非命名客户名 + customerType，累积到 state（按 name 去重 + 字典序排序）
    // 累积策略：只增不减，避免用户筛了某个客户后下拉里其他客户消失
    // 同名跨 customerType 的边界：normalize 后只有 1/2，按"首次发现"为准（业务上一个客户名一般对应一种类型）
    useEffect(() => {
        setDiscoveredOtherCustomers((prev) => {
            const namedSet = new Set<string>(CUSTOMER_NAMES);
            const known = new Map<string, number>(prev.map((c) => [c.name, c.customerType]));
            let changed = false;
            const merge = (items: ScaleDashboardItem[]) => {
                items.forEach((item) => {
                    const name = (item.customerName ?? '').trim();
                    const ct = item.customerType;
                    if (!name || namedSet.has(name) || known.has(name)) return;
                    if (ct !== 1 && ct !== 2) return; // normalize 后只接受 1/2，跳过 null / 异常值
                    known.set(name, ct);
                    changed = true;
                });
            };
            merge(rawList);
            merge(hourlyItems);
            if (!changed) return prev;
            return Array.from(known.entries())
                .map(([name, customerType]) => ({ name, customerType }))
                .sort((a, b) => a.name.localeCompare(b.name, 'zh'));
        });
    }, [rawList, hourlyItems]);

    // 下拉选项结构（扁平 + 级联过滤）：
    //   - 全部 + 命名客户（按 selectedCustomerType 过滤）+ 动态发现的非命名客户（按 selectedCustomerType 过滤，平铺到同级）
    //   - 表格内仍保留「其他客户」聚合行 + 其下钻具体客户的层级，与下拉解耦
    //   - selectedCustomerType=null（全部）：所有客户名
    //   - selectedCustomerType=1（跨境专线）：只显示 CROSS_NAMED_CUSTOMERS + customerType=1 的具体客户
    //   - selectedCustomerType=2（本地揽收）：只显示 LOCAL_NAMED_CUSTOMERS + customerType=2 的具体客户
    type CustomerNameOption = { value: string | null; label: string };
    const customerNameOptions = useMemo((): CustomerNameOption[] => {
        const includeLocal = selectedCustomerType == null || selectedCustomerType === 2;
        const includeCross = selectedCustomerType == null || selectedCustomerType === 1;
        const namedCustomers: string[] = [
            ...(includeLocal ? LOCAL_NAMED_CUSTOMERS : []),
            ...(includeCross ? CROSS_NAMED_CUSTOMERS : []),
        ];
        const filteredDiscovered = discoveredOtherCustomers.filter((c) =>
            selectedCustomerType == null || c.customerType === selectedCustomerType
        );
        return [
            { value: null, label: '全部' },
            ...namedCustomers.map((name) => ({ value: name, label: name })),
            ...filteredDiscovered.map((c) => ({ value: c.name, label: c.name })),
        ];
    }, [discoveredOtherCustomers, selectedCustomerType]);

    // 客户类型↔客户名称级联：customerType 切换后若当前 selectedCustomerName 不在新选项里，自动清空
    // - null（全部）与 '__other__'（聚合项）始终合法，不清
    // - 具体客户名必须出现在 customerNameOptions 里才保留
    useEffect(() => {
        if (selectedCustomerName == null || selectedCustomerName === '__other__') return;
        const valid = customerNameOptions.some((o) => o.value === selectedCustomerName);
        if (!valid) setSelectedCustomerName(null);
    }, [customerNameOptions, selectedCustomerName]);

    const toggleExpandOther = useCallback((key: string) => {
        setExpandedOtherKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const renderLabelWithExpand = useCallback((text: string, record: DataRow): React.ReactNode => {
        if (!record.expandable) return text;
        const isExpanded = expandedOtherKeys.has(record.key);
        return (
            <span className="inline-flex items-center">
                <span
                    className="cursor-pointer mr-1 text-gray-500 hover:text-gray-700 select-none"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpandOther(record.key);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleExpandOther(record.key);
                        }
                    }}
                    aria-label={isExpanded ? '收起' : '展开'}
                >
                    {isExpanded ? '▼' : '▶'}
                </span>
                <span>{text}</span>
            </span>
        );
    }, [expandedOtherKeys, toggleExpandOther]);

    const queryType = timeRange?.queryType ?? ScaleQueryType.DAILY;
    const rawList2 = (data2?.queryScaleDashboard?.items ?? []).map(normalizeScaleItem);

    const AVG_LABELS = ['日均', '周均', '月均'];

    const { periodColumns, avgLabel } = useMemo(() => {
        const periods = Array.from(new Set(rawList.map((item: ScaleDashboardItem) => item.timePeriod)));
        const avgLabel = timeRange
            ? resolveDashboardComparisonAvgLabel(queryType, timeRange.startDate, timeRange.endDate, timeRange.todayRealtime)
            : (queryType === ScaleQueryType.DAILY ? '日均' : queryType === ScaleQueryType.WEEKLY ? '周均' : '月均');
        const periodCols = periods
            .filter((p) => !AVG_LABELS.includes(p))
            .sort();
        return { periodColumns: periodCols, avgLabel };
    }, [rawList, queryType, timeRange]);

    const buildOrderRows = useMemo((): DataRow[] => {
        // 与 historicalMaxData skip 条件保持一致：筛了客户就不显示皇冠，避免 Apollo 缓存的旧 maxPeriods 渗漏
        const nationMaxPeriods = isCustomerFiltered
            ? undefined
            : historicalMaxData?.queryScaleDashboardHistoricalMax?.maxPeriods ?? undefined;
        const rows = orderFlowForActiveTab === 'sorting'
            ? buildSortingOnlyRowsCustomerByHub({
                items: rawList, items2: rawList2,
                subTab: ScaleDashboardSubTab.PICKUP_TREND,
                metric: 'pickup',
                periodColumns, avgLabel, isSinglePeriod, queryType,
                selectedHubId, operableHubs, hasFullWarehouseAccess,
                nationMaxPeriods,
            })
            : buildHourlySummaryCustomerRows({
                items: rawList, items2: rawList2,
                subTab: ScaleDashboardSubTab.PICKUP_TREND,
                metric: 'pickup',
                orderFlowType: OrderFlowType.NORMAL,
                expandNationHubs: true,
                enableOtherCustomerExpansion: true,
                periodColumns, avgLabel, isSinglePeriod, queryType,
                selectedHubId, singleAccessibleHubId, operableHubs, hasFullWarehouseAccess,
                selectedCustomerType, selectedCustomerName,
                nationMaxPeriods,
            });
        return filterByExpansion(filterRowsWithoutPeriodData(rows, periodColumns), expandedOtherKeys);
    }, [rawList, rawList2, periodColumns, avgLabel, isSinglePeriod, queryType, operableHubs, historicalMaxData, isCustomerFiltered, orderFlowForActiveTab, selectedHubId, singleAccessibleHubId, hasFullWarehouseAccess, selectedCustomerType, selectedCustomerName, expandedOtherKeys]);

    const pickupAnnotationDateKeys = useMemo(() => {
        // AVG 列固定用 range；WOW 列仅 DAILY 视图锚定单日 endDate，WEEKLY/MONTHLY 环比是两段 range 对比，range 才是正确签名
        const keys = new Set<string>([`${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`]);
        if (queryType === ScaleQueryType.DAILY) keys.add(timeRange?.endDate ?? '');
        periodColumns.forEach((period) => keys.add(computeScaleAnnotationDateKey(period, queryType)));
        return Array.from(keys);
    }, [periodColumns, queryType, timeRange]);
    const {
        annotationsByKey: pickupAnnotationsByKey,
        createAnnotation: createPickupAnnotation,
        updateAnnotation: updatePickupAnnotation,
        deleteAnnotation: deletePickupAnnotation,
        refetchAnnotations: refetchPickupAnnotations,
    } = useDashboardAnnotations({ pageKey: PAGE_KEY_SCALE, dateKeys: pickupAnnotationDateKeys });

    const columns: ColumnsType<DataRow> = useMemo(() => {
        const metricColumnTitle = scaleTab === 'order' ? '指标-下单包裹数' : '指标-揽收包裹数';
        const pickupBusinessType = `pickup.${orderFlowForActiveTab}`;
        const wrapWithAnnotation = (
            columnType: DashboardAnnotationColumnType,
            dateKey: string,
            record: DataRow,
            node: React.ReactNode
        ): React.ReactNode => {
            const hubMatch = record.key.match(/_hub_(\d+)/);
            const annotationCellCtx: CellContext = {
                businessType: pickupBusinessType,
                hubId: hubMatch ? hubMatch[1] : '0',
                metricKey: hubMatch ? record.key.replace(hubMatch[0], '') : record.key,
                columnType,
                dateKey,
            };
            const annotationDims = scaleAnnotationConfig.buildCellDimensions(annotationCellCtx);
            const cellAnnotations = pickupAnnotationsByKey.get(buildAnnotationKey(PAGE_KEY_SCALE, annotationDims, annotationCellCtx.dateKey, annotationCellCtx.columnType, annotationCellCtx.hubId ?? '', annotationCellCtx.metricKey));
            return (
                <MetricAnnotationCell
                    hubId={annotationCellCtx.hubId ?? ''}
                    metricKey={annotationCellCtx.metricKey}
                    dimensions={annotationDims}
                    dateKey={annotationCellCtx.dateKey}
                    columnType={annotationCellCtx.columnType}
                    annotations={cellAnnotations}
                    writeResource={scaleAnnotationConfig.writeResource}
                    adminResource={scaleAnnotationConfig.adminResource}
                    onCreate={createPickupAnnotation}
                    onUpdate={updatePickupAnnotation}
                    onDelete={deletePickupAnnotation}
                    onOpen={refetchPickupAnnotations}
                >
                    {node}
                </MetricAnnotationCell>
            );
        };
        const cols: ColumnsType<DataRow> = [
            {
                title: metricColumnTitle,
                dataIndex: 'label',
                key: 'label',
                fixed: 'left',
                width: 140,
                onCell: (record: DataRow) => {
                    const bg =
                        record.headerBg === 'deep'
                            ? PURPLE_DEEP
                            : record.headerBg === 'mid'
                                ? PURPLE_MID
                                : record.headerBg === 'light'
                                    ? PURPLE_LIGHT
                                    : undefined;
                    const paddingLeft = 8 + record.indent * 4 * 8;
                    return { style: { backgroundColor: bg, paddingLeft } };
                },
                render: renderLabelWithExpand,
            },
        ];

        periodColumns.forEach((period) => {
            // 处理日期范围的换行：只在需要时换行，优先在~后换行
            const formatPeriodTitle = (periodStr: string) => {
                if (periodStr.includes('~')) {
                    // 使用word-break让文本在~后优先换行
                    // 当单元格足够宽时，保持在一行
                    return (
                        <div style={{
                            whiteSpace: 'normal',
                            wordBreak: 'keep-all',
                            lineHeight: '1.4',
                            textAlign: 'center'
                        }}>
                            {periodStr.replace('~', '~\u200B')}
                        </div>
                    );
                }
                return periodStr;
            };

            cols.push({
                title: formatPeriodTitle(period),
                dataIndex: period,
                key: period,
                width: 145,
                align: 'right',
                render: (val: number | string | undefined, record: DataRow) => {
                    const dateKey = computeScaleAnnotationDateKey(period, queryType);
                    if (val === undefined || val === null) return wrapWithAnnotation('RAW', dateKey, record, '-');

                    // 仅在「全美」这一行，并且该日期为全美历史最高值时，添加皇冠标记
                    const anyRecord = record as any;
                    const nationMaxPeriods: string[] | undefined = anyRecord?.__nationMaxPeriods;
                    const isNationRow = NATION_ROW_KEYS.has(record.key);
                    const isMaxForNation =
                        isNationRow &&
                        Array.isArray(nationMaxPeriods) &&
                        nationMaxPeriods.includes(period) &&
                        typeof val === 'number';

                    if (isMaxForNation) {
                        return wrapWithAnnotation('RAW', dateKey, record, (
                            <span>
                                <Tooltip title="历史最高">
                                    <CrownFilled style={{ marginRight: 4, color: '#faad14' }} />
                                </Tooltip>
                                {formatNumber(val)}
                            </span>
                        ));
                    }

                    if (typeof val === 'string') return wrapWithAnnotation('RAW', dateKey, record, val);
                    return wrapWithAnnotation('RAW', dateKey, record, formatNumber(val));
                },
                onCell: () => ({ className: 'group/annot' }),
            });
        });

        cols.push({
            title: avgLabel,
            dataIndex: avgLabel,
            key: avgLabel,
            width: 145,
            align: 'right',
            render: (val: number | string | undefined, record: DataRow) => {
                const dateKey = `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
                if (val === undefined || val === null) return wrapWithAnnotation('AVG', dateKey, record, '-');
                if (typeof val === 'string') return wrapWithAnnotation('AVG', dateKey, record, val);
                // 四舍五入保留整数
                return wrapWithAnnotation('AVG', dateKey, record, formatNumber(Math.round(val)));
            },
            onCell: () => ({ className: 'group/annot' }),
        });

        cols.push({
            title: queryType === ScaleQueryType.DAILY ? '周环比' : '环比',
            dataIndex: '环比',
            key: '环比',
            width: 135,
            align: 'right',
            render: (val: string | undefined, record: DataRow) => {
                const dateKey = queryType === ScaleQueryType.DAILY
                    ? (timeRange?.endDate ?? '')
                    : `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
                return wrapWithAnnotation('WOW', dateKey, record, formatChange(val));
            },
            onCell: () => ({ className: 'group/annot' }),
        });

        return cols;
    }, [periodColumns, avgLabel, scaleTab, queryType, renderLabelWithExpand, orderFlowForActiveTab, selectedCustomerName, timeRange, pickupAnnotationsByKey, createPickupAnnotation, updatePickupAnnotation, deletePickupAnnotation]);

    const orderTableData = useMemo(() => {
        return buildOrderRows.map((row) => ({
            ...row,
            [avgLabel]: row[avgLabel] ?? row.日均 ?? row.周均 ?? row.月均,
        }));
    }, [buildOrderRows, avgLabel]);

    // ===== 小时汇总看板数据 =====
    const hourlyIsSinglePeriod = useMemo((): boolean => {
        if (!timeRange) return false;
        const qt = timeRange.queryType;
        const start = safeParsePeriodInTz(timeRange.startDate, DASHBOARD_PACIFIC_TZ);
        const end = safeParsePeriodInTz(timeRange.endDate, DASHBOARD_PACIFIC_TZ);
        if (!start || !end) return false;
        const daysInRange = end.diff(start, 'day') + 1;
        return (
            (qt === ScaleQueryType.DAILY && start.isSame(end, 'day')) ||
            (qt === ScaleQueryType.WEEKLY && daysInRange <= 7) ||
            (qt === ScaleQueryType.MONTHLY && start.isSame(end, 'month')) ||
            timeRange.todayRealtime === true
        );
    }, [timeRange]);

    const { hourlyPeriodColumns, hourlyAvgLabel } = useMemo(() => {
        const qt = timeRange?.queryType ?? ScaleQueryType.DAILY;
        const label = timeRange
            ? resolveDashboardComparisonAvgLabel(qt, timeRange.startDate, timeRange.endDate, timeRange.todayRealtime)
            : (qt === ScaleQueryType.DAILY ? '日均' : qt === ScaleQueryType.WEEKLY ? '周均' : '月均');
        const periods = Array.from(new Set(hourlyItems.map((i) => i.timePeriod)))
            .filter((p) => !['日均', '周均', '月均'].includes(p))
            .sort();
        return { hourlyPeriodColumns: periods, hourlyAvgLabel: label };
    }, [hourlyItems, timeRange]);

    // 与 historicalMaxData skip 条件保持一致：筛了客户就不显示皇冠
    const hourlyNationMaxPeriods = isCustomerFiltered
        ? undefined
        : historicalMaxData?.queryScaleDashboardHistoricalMax?.maxPeriods ?? undefined;

    const hourlyCustomerOrderRows = useMemo((): DataRow[] => {
        if (orderSubTab !== 'customer') return [];
        const rows = orderFlowTabCustomerOrder === 'sorting'
            ? buildSortingOnlyRowsCustomerByHub({
                items: hourlyItems, items2: hourlyItems2,
                subTab: ScaleDashboardSubTab.CUSTOMER_ORDER,
                metric: 'order',
                periodColumns: hourlyPeriodColumns, avgLabel: hourlyAvgLabel,
                isSinglePeriod: hourlyIsSinglePeriod, queryType: timeRange?.queryType ?? ScaleQueryType.DAILY,
                selectedHubId, operableHubs, hasFullWarehouseAccess,
                nationMaxPeriods: hourlyNationMaxPeriods,
            })
            : buildHourlySummaryCustomerRows({
                items: hourlyItems, items2: hourlyItems2,
                subTab: ScaleDashboardSubTab.CUSTOMER_ORDER,
                metric: 'order',
                orderFlowType: OrderFlowType.NORMAL,
                expandNationHubs: true,
                enableOtherCustomerExpansion: true,
                periodColumns: hourlyPeriodColumns, avgLabel: hourlyAvgLabel,
                isSinglePeriod: hourlyIsSinglePeriod, queryType: timeRange?.queryType ?? ScaleQueryType.DAILY,
                selectedHubId, singleAccessibleHubId, operableHubs, hasFullWarehouseAccess,
                selectedCustomerType, selectedCustomerName,
                nationMaxPeriods: hourlyNationMaxPeriods,
            });
        return filterByExpansion(filterRowsWithoutPeriodData(rows, hourlyPeriodColumns), expandedOtherKeys);
    }, [hourlyItems, hourlyItems2, hourlyPeriodColumns, hourlyAvgLabel, hourlyIsSinglePeriod, timeRange, orderSubTab, orderFlowTabCustomerOrder, selectedHubId, singleAccessibleHubId, operableHubs, hasFullWarehouseAccess, selectedCustomerType, selectedCustomerName, hourlyNationMaxPeriods, expandedOtherKeys]);

    const hourlyPickupOrderRows = useMemo((): DataRow[] => {
        if (orderSubTab !== 'pickup') return [];
        const rows = orderFlowTabPickupOrder === 'sorting'
            ? buildHourlySummaryPickupSortingOnlyRows({
                items: hourlyItems, items2: hourlyItems2,
                metric: 'order',
                periodColumns: hourlyPeriodColumns, avgLabel: hourlyAvgLabel,
                isSinglePeriod: hourlyIsSinglePeriod, queryType: timeRange?.queryType ?? ScaleQueryType.DAILY,
                operableHubs,
                nationMaxPeriods: hourlyNationMaxPeriods,
            })
            : buildHourlySummaryCustomerRows({
                items: hourlyItems, items2: hourlyItems2,
                subTab: ScaleDashboardSubTab.PICKUP_ORDER,
                metric: 'order',
                orderFlowType: OrderFlowType.NORMAL,
                simplifiedCustomerTypeView: true,
                periodColumns: hourlyPeriodColumns, avgLabel: hourlyAvgLabel,
                isSinglePeriod: hourlyIsSinglePeriod, queryType: timeRange?.queryType ?? ScaleQueryType.DAILY,
                selectedHubId, singleAccessibleHubId, operableHubs, hasFullWarehouseAccess,
                selectedCustomerType, selectedCustomerName,
                nationMaxPeriods: hourlyNationMaxPeriods,
            });
        return filterRowsWithoutPeriodData(rows, hourlyPeriodColumns);
    }, [hourlyItems, hourlyItems2, hourlyPeriodColumns, hourlyAvgLabel, hourlyIsSinglePeriod, timeRange, orderSubTab, orderFlowTabPickupOrder, selectedHubId, singleAccessibleHubId, operableHubs, hasFullWarehouseAccess, selectedCustomerType, selectedCustomerName, hourlyNationMaxPeriods]);

    const hourlyLastMileRows = useMemo((): DataRow[] => {
        if (orderSubTab !== 'lastMile') return [];
        const rows = buildHourlySummaryLastMileRows({
            items: hourlyItems, items2: hourlyItems2,
            metric: 'order',
            periodColumns: hourlyPeriodColumns, avgLabel: hourlyAvgLabel,
            isSinglePeriod: hourlyIsSinglePeriod, queryType: timeRange?.queryType ?? ScaleQueryType.DAILY,
            operableHubs,
            nationMaxPeriods: hourlyNationMaxPeriods,
        });
        return filterRowsWithoutPeriodData(rows, hourlyPeriodColumns);
    }, [hourlyItems, hourlyItems2, hourlyPeriodColumns, hourlyAvgLabel, hourlyIsSinglePeriod, timeRange, orderSubTab, selectedHubId, operableHubs, hourlyNationMaxPeriods]);

    // 当前选中的排序 period 已不在列里（如切换时间范围），清除排序状态
    useEffect(() => {
        if (lastMileSort && !hourlyPeriodColumns.includes(lastMileSort.period)) {
            setLastMileSort(null);
        }
    }, [hourlyPeriodColumns, lastMileSort]);

    // 尾程下单监控按 period 列对 whs 行排序：hub 行顺序不变，全美行固定第一
    const sortedHourlyLastMileRows = useMemo((): DataRow[] => {
        if (!lastMileSort) return hourlyLastMileRows;
        const { period, order } = lastMileSort;
        const dir = order === 'descend' ? -1 : 1;
        const result: DataRow[] = [];
        let group: DataRow[] = [];
        const flushGroup = () => {
            if (group.length === 0) return;
            if (group.length === 1) {
                result.push(group[0]);
            } else {
                const [hubRow, ...whsRows] = group;
                whsRows.sort((a, b) => {
                    const va = Number((a as Record<string, unknown>)[period]) || 0;
                    const vb = Number((b as Record<string, unknown>)[period]) || 0;
                    return va === vb ? 0 : (va < vb ? -dir : dir);
                });
                result.push(hubRow, ...whsRows);
            }
            group = [];
        };
        for (const row of hourlyLastMileRows) {
            if (row.key === 'lastmile_nation') {
                result.push(row);
                continue;
            }
            if (row.indent === 0) {
                flushGroup();
                group = [row];
            } else {
                group.push(row);
            }
        }
        flushGroup();
        return result;
    }, [hourlyLastMileRows, lastMileSort]);

    const orderAnnotationDateKeys = useMemo(() => {
        // AVG 列固定用 range；WOW 列仅 DAILY 视图锚定单日 endDate，WEEKLY/MONTHLY 环比是两段 range 对比，range 才是正确签名
        const keys = new Set<string>([`${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`]);
        if ((timeRange?.queryType ?? ScaleQueryType.DAILY) === ScaleQueryType.DAILY) keys.add(timeRange?.endDate ?? '');
        hourlyPeriodColumns.forEach((period) => keys.add(computeScaleAnnotationDateKey(period, queryType)));
        return Array.from(keys);
    }, [hourlyPeriodColumns, queryType, timeRange]);
    const {
        annotationsByKey: orderAnnotationsByKey,
        createAnnotation: createOrderAnnotation,
        updateAnnotation: updateOrderAnnotation,
        deleteAnnotation: deleteOrderAnnotation,
        refetchAnnotations: refetchOrderAnnotations,
    } = useDashboardAnnotations({ pageKey: PAGE_KEY_SCALE, dateKeys: orderAnnotationDateKeys });

    const hourlyColumns: ColumnsType<DataRow> = useMemo(() => {
        const isPartialDayColumn = (p: string): boolean => {
            if (timeRange?.todayRealtime) return true;
            const startH = timeRange?.startHour ?? 0;
            const endH = timeRange?.endHour ?? 23;
            if (startH === 0 && endH === 23) return false;
            const sd = timeRange?.startDate;
            const ed = timeRange?.endDate;
            if (p === sd && p === ed) return startH > 0 || endH < 23;
            if (p === sd && startH > 0) return true;
            if (p === ed && endH < 23) return true;
            return false;
        };

        const orderBusinessType = `order.${orderSubTab}.${orderFlowForActiveTab}`;
        const wrapWithAnnotation = (
            columnType: DashboardAnnotationColumnType,
            dateKey: string,
            record: DataRow,
            node: React.ReactNode
        ): React.ReactNode => {
            const hubMatch = record.key.match(/_hub_(\d+)/);
            const annotationCellCtx: CellContext = {
                businessType: orderBusinessType,
                hubId: hubMatch ? hubMatch[1] : '0',
                metricKey: hubMatch ? record.key.replace(hubMatch[0], '') : record.key,
                columnType,
                dateKey,
            };
            const annotationDims = scaleAnnotationConfig.buildCellDimensions(annotationCellCtx);
            const cellAnnotations = orderAnnotationsByKey.get(buildAnnotationKey(PAGE_KEY_SCALE, annotationDims, annotationCellCtx.dateKey, annotationCellCtx.columnType, annotationCellCtx.hubId ?? '', annotationCellCtx.metricKey));
            return (
                <MetricAnnotationCell
                    hubId={annotationCellCtx.hubId ?? ''}
                    metricKey={annotationCellCtx.metricKey}
                    dimensions={annotationDims}
                    dateKey={annotationCellCtx.dateKey}
                    columnType={annotationCellCtx.columnType}
                    annotations={cellAnnotations}
                    writeResource={scaleAnnotationConfig.writeResource}
                    adminResource={scaleAnnotationConfig.adminResource}
                    onCreate={createOrderAnnotation}
                    onUpdate={updateOrderAnnotation}
                    onDelete={deleteOrderAnnotation}
                    onOpen={refetchOrderAnnotations}
                >
                    {node}
                </MetricAnnotationCell>
            );
        };

        const cols: ColumnsType<DataRow> = [
            {
                title: '指标-下单包裹数',
                dataIndex: 'label',
                key: 'label',
                fixed: 'left',
                width: 140,
                onCell: (record: DataRow) => {
                    const bg = record.headerBg === 'deep' ? PURPLE_DEEP : record.headerBg === 'mid' ? PURPLE_MID : record.headerBg === 'light' ? PURPLE_LIGHT : undefined;
                    const paddingLeft = 8 + record.indent * 4 * 8;
                    return { style: { backgroundColor: bg, paddingLeft } };
                },
                render: renderLabelWithExpand,
            },
        ];
        hourlyPeriodColumns.forEach((period) => {
            const formatPeriodTitle = (periodStr: string): React.ReactNode => {
                if (periodStr.includes('~')) {
                    return (
                        <div style={{ whiteSpace: 'normal', wordBreak: 'keep-all', lineHeight: '1.4', textAlign: 'center' }}>
                            {periodStr.replace('~', '~​')}
                        </div>
                    );
                }
                return periodStr;
            };
            let titleContent: React.ReactNode = formatPeriodTitle(period);
            if (isPartialDayColumn(period)) {
                titleContent = (
                    <span className="inline-flex items-center gap-1">
                        {titleContent}
                        <Tooltip title="该天不满24小时">
                            <InfoCircleOutlined style={{ fontSize: 12, color: '#faad14', cursor: 'default' }} />
                        </Tooltip>
                    </span>
                );
            }
            if (orderSubTab === 'lastMile') {
                const isActive = lastMileSort?.period === period;
                const activeOrder = isActive ? lastMileSort?.order : null;
                const onSort = (e: React.MouseEvent | React.KeyboardEvent) => {
                    e.stopPropagation();
                    setLastMileSort(prev => {
                        if (prev?.period !== period) return { period, order: 'descend' };
                        if (prev.order === 'descend') return { period, order: 'ascend' };
                        return null;
                    });
                };
                titleContent = (
                    <div className="inline-flex items-center gap-1">
                        {titleContent}
                        <Tooltip title={`按 ${period} 排序`}>
                            <span
                                className={`cursor-pointer ${isActive ? 'text-blue-500 hover:text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                                onClick={onSort}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onSort(e);
                                    }
                                }}
                            >
                                {activeOrder === 'ascend' ? <CaretUpOutlined /> : <CaretDownOutlined />}
                            </span>
                        </Tooltip>
                    </div>
                );
            }
            cols.push({
                title: titleContent,
                dataIndex: period,
                key: period,
                width: 145,
                align: 'right',
                render: (val: number | string | undefined, record: DataRow) => {
                    const dateKey = computeScaleAnnotationDateKey(period, queryType);
                    if (val === undefined || val === null) return wrapWithAnnotation('RAW', dateKey, record, '-');
                    // 全美行该日期为历史最高时，加皇冠标记（全集见 NATION_ROW_KEYS）
                    const anyRecord = record as any;
                    const nationMaxPeriods: string[] | undefined = anyRecord?.__nationMaxPeriods;
                    const isNationRow = NATION_ROW_KEYS.has(record.key);
                    const isMaxForNation =
                        isNationRow &&
                        Array.isArray(nationMaxPeriods) &&
                        nationMaxPeriods.includes(period) &&
                        typeof val === 'number';
                    if (isMaxForNation) {
                        return wrapWithAnnotation('RAW', dateKey, record, (
                            <span>
                                <Tooltip title="历史最高">
                                    <CrownFilled style={{ marginRight: 4, color: '#faad14' }} />
                                </Tooltip>
                                {formatNumber(val)}
                            </span>
                        ));
                    }
                    if (typeof val === 'string') return wrapWithAnnotation('RAW', dateKey, record, val);
                    return wrapWithAnnotation('RAW', dateKey, record, formatNumber(val));
                },
                onCell: () => ({ className: 'group/annot' }),
            });
        });
        cols.push({
            title: hourlyAvgLabel,
            dataIndex: hourlyAvgLabel,
            key: hourlyAvgLabel,
            width: 145,
            align: 'right',
            render: (val: number | string | undefined, record: DataRow) => {
                const dateKey = `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
                if (val === undefined || val === null) return wrapWithAnnotation('AVG', dateKey, record, '-');
                if (typeof val === 'string') return wrapWithAnnotation('AVG', dateKey, record, val);
                return wrapWithAnnotation('AVG', dateKey, record, formatNumber(Math.round(val)));
            },
            onCell: () => ({ className: 'group/annot' }),
        });
        cols.push({
            title: (timeRange?.queryType ?? ScaleQueryType.DAILY) === ScaleQueryType.DAILY ? '周环比' : '环比',
            dataIndex: '环比',
            key: '环比',
            width: 135,
            align: 'right',
            render: (val: string | undefined, record: DataRow) => {
                const qt = timeRange?.queryType ?? ScaleQueryType.DAILY;
                const dateKey = qt === ScaleQueryType.DAILY
                    ? (timeRange?.endDate ?? '')
                    : `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
                return wrapWithAnnotation('WOW', dateKey, record, formatChange(val));
            },
            onCell: () => ({ className: 'group/annot' }),
        });
        return cols;
    }, [hourlyPeriodColumns, hourlyAvgLabel, timeRange, orderSubTab, lastMileSort, renderLabelWithExpand, queryType, orderFlowForActiveTab, selectedCustomerName, orderAnnotationsByKey, createOrderAnnotation, updateOrderAnnotation, deleteOrderAnnotation]);

    const makeHourlyTableData = (rows: DataRow[]) =>
        rows.map((row) => ({ ...row, [hourlyAvgLabel]: row[hourlyAvgLabel] ?? row.日均 ?? row.周均 ?? row.月均 }));

    /** 当前选中 tab 对应的导出 payload（与页面表格 1:1 同口径） */
    const exportPayload = useMemo((): {
        rows: DataRow[];
        periods: string[];
        avgCol: string;
        sheetName: string;
        fileNamePrefix: string;
    } | null => {
        if (scaleTab === 'pickup') {
            return {
                rows: orderTableData,
                periods: periodColumns,
                avgCol: avgLabel,
                sheetName: orderFlowForActiveTab === 'sorting' ? '揽收趋势（仅揽收分拣）' : '揽收趋势（揽收分拣+尾程）',
                fileNamePrefix: orderFlowForActiveTab === 'sorting' ? 'Scale_Pickup_PickupSortingOnly' : 'Scale_Pickup_PickupSortingDelivery',
            };
        }
        if (orderSubTab === 'customer') {
            return {
                rows: makeHourlyTableData(hourlyCustomerOrderRows),
                periods: hourlyPeriodColumns,
                avgCol: hourlyAvgLabel,
                sheetName: orderFlowTabCustomerOrder === 'sorting' ? '客户下单监控（仅揽收分拣）' : '客户下单监控（揽收分拣+尾程）',
                fileNamePrefix: orderFlowTabCustomerOrder === 'sorting' ? 'Scale_CustomerOrder_PickupSortingOnly' : 'Scale_CustomerOrder_PickupSortingDelivery',
            };
        }
        if (orderSubTab === 'pickup') {
            return {
                rows: makeHourlyTableData(hourlyPickupOrderRows),
                periods: hourlyPeriodColumns,
                avgCol: hourlyAvgLabel,
                sheetName: orderFlowTabPickupOrder === 'sorting' ? '揽收下单监控（仅揽收分拣）' : '揽收下单监控（揽收分拣+尾程）',
                fileNamePrefix: orderFlowTabPickupOrder === 'sorting' ? 'Scale_PickupOrder_PickupSortingOnly' : 'Scale_PickupOrder_PickupSortingDelivery',
            };
        }
        return {
            rows: makeHourlyTableData(sortedHourlyLastMileRows),
            periods: hourlyPeriodColumns,
            avgCol: hourlyAvgLabel,
            sheetName: '尾程下单监控',
            fileNamePrefix: 'Scale_LastMileOrder',
        };
    // makeHourlyTableData 是普通函数，闭包内捕获 hourlyAvgLabel；依赖项已包含
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        scaleTab, orderSubTab,
        orderTableData, periodColumns, avgLabel, orderFlowForActiveTab,
        hourlyCustomerOrderRows, hourlyPickupOrderRows, sortedHourlyLastMileRows,
        hourlyPeriodColumns, hourlyAvgLabel,
        orderFlowTabCustomerOrder, orderFlowTabPickupOrder,
    ]);

    const handleDownloadScale = useCallback(async () => {
        if (!timeRange) {
            message.warning('请先选择时间范围');
            return;
        }
        if (exportLoading) {
            message.warning('导出任务生成中，请稍候');
            return;
        }
        if (!exportPayload || exportPayload.rows.length === 0 || exportPayload.periods.length === 0) {
            message.warning('当前条件下无数据可导出');
            return;
        }

        const indentPrefix = (indent: 0 | 1 | 2 | 3): string => '    '.repeat(indent);
        const qt = (timeRange.queryType ?? ScaleQueryType.DAILY) as ScaleQueryType;
        const changeColLabel = qt === ScaleQueryType.DAILY ? '周环比' : '环比';
        const periodHeaders = [...exportPayload.periods, exportPayload.avgCol, changeColLabel];
        const headers = ['指标', ...periodHeaders];
        const mainRows = exportPayload.rows.map((row) =>
            headers.map((h) => {
                if (h === '指标') {
                    return dashboardTableExportCellString(`${indentPrefix(row.indent)}${row.label}`);
                }
                const dataKey = h === '周环比' ? '环比' : h;
                return dashboardTableExportCellString((row as Record<string, unknown>)[dataKey]);
            })
        );

        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const auditSummary =
                    `scale tab=${scaleTab}${scaleTab === 'order' ? ` subTab=${orderSubTab}` : ''} ` +
                    `orderFlow=${orderFlowForActiveTab} queryType=${timeRange.queryType} ` +
                    `${timeRange.startDate}..${timeRange.endDate} hubId=${selectedHubId}`;
                const response = await generateDashboardTableExport({
                    variables: {
                        input: {
                            fileNamePrefix: exportPayload.fileNamePrefix,
                            timezone: userTimezone,
                            snapshotSheetName: exportPayload.sheetName,
                            mainSheetName: exportPayload.sheetName,
                            snapshotRows: null,
                            mainColumnHeaders: headers,
                            mainRows,
                            auditSummary,
                        },
                    },
                });
                const fileName = response.data?.generateDashboardTableExport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error('导出失败，请稍后重试');
                }
            } catch (error: unknown) {
                showError(error);
            }
        });
    }, [
        timeRange,
        exportPayload,
        scaleTab,
        orderSubTab,
        orderFlowForActiveTab,
        selectedHubId,
        generateDashboardTableExport,
        exportLoading,
        runExportWithCooldown,
    ]);

    return (
        <div className="p-6 min-h-full flex flex-col">
            <style>{`
                .scale-dashboard-tabs .ant-tabs-ink-bar { background: #7c3aed !important; }
                .scale-order-flow-segmented.ant-segmented .ant-segmented-item {
                    font-weight: 600;
                    min-height: 40px;
                    line-height: 38px;
                    padding-inline: 18px;
                }
                .scale-order-flow-segmented.ant-segmented .ant-segmented-item-selected {
                    background: #7c3aed !important;
                    color: #fff !important;
                    box-shadow: none !important;
                }
                .scale-order-flow-segmented.ant-segmented .ant-segmented-item-selected .ant-segmented-item-label {
                    color: #fff !important;
                }
                .scale-order-flow-segmented.ant-segmented .ant-segmented-thumb {
                    background: #7c3aed !important;
                    box-shadow: 0 1px 3px rgba(124, 58, 237, 0.45);
                }
                .scale-dashboard-table .ant-table-thead > tr > th {
                    white-space: normal !important;
                    word-break: keep-all;
                    line-height: 1.4;
                    text-align: center;
                }
                .scale-dashboard-table .ant-table-thead > tr > th:first-child {
                    text-align: left;
                }
            `}</style>
            <Card title={
                <span className="text-xl font-semibold">
                    规模数据看板
                    <span className="text-sm font-normal ml-3">（时区说明: 日期均为美西时区）</span>
                </span>
            } bordered={false} className="flex-1">
                {/* 两行筛选区合并到同一个 grid，TimeRangeFilter 内部也使用相同规格的 grid-cols-3 gap-4，列宽自动对齐 */}
                {/* max-w 限制大屏下不过度拉伸；小屏 grid 仍按 1fr 等分 */}
                <div className="mb-4 grid grid-cols-3 gap-4 gap-y-3 max-w-screen-xl">
                    {/* 第一行：TimeRangeFilter 占满 3 列 */}
                    <div className="col-span-3">
                        <TimeRangeFilter
                            onChange={handleTimeRangeChange}
                            maxDaysBack={365}
                            showHourRange={true}
                            dashboardTimezone={DASHBOARD_PACIFIC_TZ}
                        />
                    </div>
                    {/* 第二行：分拣仓 / 客户类型 / 客户名称 */}
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 whitespace-nowrap">分拣仓名称</span>
                        <Select
                            className="flex-1 min-w-0"
                            value={selectedHubId}
                            onChange={(v) => setSelectedHubId(v)}
                            options={hubOptions}
                            loading={hubsLoading}
                            placeholder="选择分拣仓"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 whitespace-nowrap">客户类型</span>
                        <Select
                            className="flex-1 min-w-0"
                            value={selectedCustomerType}
                            onChange={(v) => setSelectedCustomerType(v)}
                            options={CUSTOMER_TYPE_OPTIONS}
                            placeholder="全部"
                            disabled={orderFlowForActiveTab === 'sorting'}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 whitespace-nowrap">客户名称</span>
                        <Select
                            className="flex-1 min-w-0"
                            value={selectedCustomerName}
                            onChange={(v) => setSelectedCustomerName(v)}
                            options={customerNameOptions}
                            placeholder="全部"
                            disabled={orderFlowForActiveTab === 'sorting'}
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </div>
                </div>

                <Tabs
                    activeKey={scaleTab}
                    onChange={(k) => setScaleTab(k as 'order' | 'pickup')}
                    className="scale-dashboard-tabs"
                    tabBarExtraContent={{
                        right: (
                            <Button
                                type="primary"
                                onClick={() => void handleDownloadScale()}
                                loading={exportLoading}
                                disabled={exportLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                    {getExportButtonLabel()}
                                </span>
                            </Button>
                        ),
                    }}
                    items={[
                        {
                            key: 'order',
                            label: '下单趋势',
                            children: (
                                <Spin spinning={hourlyLoading}>
                                    <Tabs
                                        activeKey={orderSubTab}
                                        onChange={(k) => setOrderSubTab(k as 'customer' | 'pickup' | 'lastMile')}
                                        items={[
                                            {
                                                key: 'customer',
                                                label: '客户下单监控',
                                                children: (
                                                    <>
                                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                                            <span className="font-semibold text-gray-800 whitespace-nowrap">订单类型</span>
                                                            <Segmented
                                                                size="large"
                                                                className="scale-order-flow-segmented"
                                                                value={orderFlowTabCustomerOrder}
                                                                onChange={(v) => handleOrderFlowTabCustomerOrderChange(v as 'normal' | 'sorting')}
                                                                options={[
                                                                    { label: '揽收分拣+尾程', value: 'normal' },
                                                                    { label: '仅揽收分拣', value: 'sorting' },
                                                                ]}
                                                            />
                                                        </div>
                                                        <p className="text-black mb-2">
                                                            {orderSubTabNote}数据更新于：{hourlyDataUpdatedAt}
                                                        </p>
                                                        <Table
                                                            columns={hourlyColumns}
                                                            dataSource={makeHourlyTableData(hourlyCustomerOrderRows)}
                                                            rowKey="key"
                                                            pagination={false}
                                                            scroll={{ x: 'max-content' }}
                                                            sticky
                                                            bordered
                                                            size="middle"
                                                            className="scale-dashboard-table mt-4"
                                                        />
                                                    </>
                                                ),
                                            },
                                            {
                                                key: 'pickup',
                                                label: '揽收下单监控',
                                                children: (
                                                    <>
                                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                                            <span className="font-semibold text-gray-800 whitespace-nowrap">订单类型</span>
                                                            <Segmented
                                                                size="large"
                                                                className="scale-order-flow-segmented"
                                                                value={orderFlowTabPickupOrder}
                                                                onChange={(v) => handleOrderFlowTabPickupOrderChange(v as 'normal' | 'sorting')}
                                                                options={[
                                                                    { label: '揽收分拣+尾程', value: 'normal' },
                                                                    { label: '仅揽收分拣', value: 'sorting' },
                                                                ]}
                                                            />
                                                        </div>
                                                        <p className="text-black mb-2">
                                                            {orderSubTabNote}数据更新于：{hourlyDataUpdatedAt}
                                                        </p>
                                                        <Table
                                                            columns={hourlyColumns}
                                                            dataSource={makeHourlyTableData(hourlyPickupOrderRows)}
                                                            rowKey="key"
                                                            pagination={false}
                                                            scroll={{ x: 'max-content' }}
                                                            sticky
                                                            bordered
                                                            size="middle"
                                                            className="scale-dashboard-table mt-4"
                                                        />
                                                    </>
                                                ),
                                            },
                                            {
                                                key: 'lastMile',
                                                label: '尾程下单监控',
                                                children: (
                                                    <>
                                                        <p className="text-black mb-2">
                                                            {orderSubTabNote}数据更新于：{hourlyDataUpdatedAt}
                                                        </p>
                                                        <Table
                                                            columns={hourlyColumns}
                                                            dataSource={makeHourlyTableData(sortedHourlyLastMileRows)}
                                                            rowKey="key"
                                                            pagination={false}
                                                            scroll={{ x: 'max-content' }}
                                                            sticky
                                                            bordered
                                                            size="middle"
                                                            className="scale-dashboard-table mt-4"
                                                        />
                                                    </>
                                                ),
                                            },
                                        ]}
                                    />
                                </Spin>
                            ),
                        },
                        {
                            key: 'pickup',
                            label: '揽收趋势',
                            children: (
                                <Spin spinning={loading}>
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className="font-semibold text-gray-800 whitespace-nowrap">订单类型</span>
                                        <Segmented
                                            size="large"
                                            className="scale-order-flow-segmented"
                                            value={orderFlowTabPickup}
                                            onChange={(v) => handleOrderFlowTabPickupChange(v as 'normal' | 'sorting')}
                                            options={[
                                                { label: '揽收分拣+尾程', value: 'normal' },
                                                { label: '仅揽收分拣', value: 'sorting' },
                                            ]}
                                        />
                                    </div>
                                    <p className="text-black mb-2">
                                        按客户 × 揽收HUB拆解揽收量，用于监控各HUB揽收量是否异常。数据更新于：{pickupDataUpdatedAt}
                                    </p>
                                    <Table
                                        columns={columns}
                                        dataSource={orderTableData}
                                        rowKey="key"
                                        pagination={false}
                                        scroll={{ x: 'max-content' }}
                                        sticky
                                        bordered
                                        size="middle"
                                        className="scale-dashboard-table mt-4"
                                    />
                                </Spin>
                            ),
                        },
                    ]}
                />
            </Card>
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.scale}
            />
        </div>
    );
};

export default ScaleDataPage;